import React, { useState, useEffect, useCallback, useRef } from 'react';
import CopilotWidget from '../components/CopilotWidget';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import { aiApi, type PestScanResult } from '../services/aiApi';
import LogoutButton from '../components/LogoutButton';
import ProfileButton from '../components/ProfileButton';
import PolygonCaptureMap, { PolygonCaptureHandle } from '../components/PolygonCaptureMap';
import FarmPolygonViewer from '../components/FarmPolygonViewer';
import WeatherWidget from '../components/WeatherWidget';
import DiseaseRiskCard from '../components/DiseaseRiskCard';
import AdvisoryTimelineCard from '../components/AdvisoryTimelineCard';
import ProductScanner from '../components/ProductScanner';
import {
  BrandMark, Card, SectionHeader, MetricTile, SmartStatus, StatusPill,
  Spinner, EmptyState, Alert, IconButton, Sheet, Avatar, SegmentedTabs,
  Pagination, usePagination,
} from '../components/ui';

// ─── Inline enterprise icon set ───
const Icon = {
  Package: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" />
    </svg>
  ),
  Dollar: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
  Home: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 11.5L12 4l9 7.5" /><path d="M5 10.5V21h14V10.5" /><path d="M9 21v-6h6v6" />
    </svg>
  ),
  Copy: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  ),
  Truck: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 6h11v10H3z" /><path d="M14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" />
    </svg>
  ),
  QrCode: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="3" width="6" height="6" rx="1" /><rect x="15" y="3" width="6" height="6" rx="1" /><rect x="3" y="15" width="6" height="6" rx="1" /><path d="M15 15h2v2h-2zM19 15h2M19 19h2v2M15 19v2h2" />
    </svg>
  ),
  Award: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="8" r="6" /><path d="M15.5 13.5L17 22l-5-3-5 3 1.5-8.5" />
    </svg>
  ),
  Calendar: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ),
  Clock: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  ),
  Check: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  Alert: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M10.3 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" />
    </svg>
  ),
  Refresh: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M23 4v6h-6M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </svg>
  ),
  MapPin: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  ),
  Phone: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  User: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  ),
  FileCheck: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6M9 15l2 2 4-4" />
    </svg>
  ),
  Shield: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  Wallet: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M21 12V7H5a2 2 0 010-4h14v4M3 5v14a2 2 0 002 2h16v-5M18 12a2 2 0 000 4h4v-4z" />
    </svg>
  ),
  Send: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  ),
  X: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  History: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 3v5h5M3.05 13A9 9 0 106 5.3L3 8" /><path d="M12 7v5l4 2" />
    </svg>
  ),
  ArrowUpRight: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M7 17L17 7M7 7h10v10" />
    </svg>
  ),
  ArrowDownRight: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M7 7l10 10M17 7v10H7" />
    </svg>
  ),
  BookOpen: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
    </svg>
  ),
  Leaf: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19.5 2c-.5 4.5-.5 7.5-3 12-2 3.5-7 4-7 4zM2 21c0-3 1.85-5.36 5.08-6" />
    </svg>
  ),
  Activity: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  // v1.8.1: TrendingUp icon for yield forecast tab
  TrendingUp: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M23 6l-9.5 9.5-5-5L1 18M17 6h6v6" />
    </svg>
  ),
  Map: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M1 6v15l7-3 8 3 7-3V3l-7 3-8-3-7 3z" /><path d="M8 3v15M16 6v15" />
    </svg>
  ),
  Plus: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  Layers: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),
  Trash: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  ),
  // v1.7.6: Info icon for the "contact your field agent" notice
  Info: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  ),
  // v3.2.23: Added Sparkles icon (used by SmartAdvisorySummaryCard + AgronomistAdvisoryCard)
  Sparkles: (p: any) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
      <path d="M19 14l.7 2.1L22 17l-2.3.9L19 20l-.7-2.1L16 17l2.3-.9L19 14z" />
      <path d="M5 14l.7 2.1L8 17l-2.3.9L5 20l-.7-2.1L2 17l2.3-.9L5 14z" />
    </svg>
  ),
};

const FarmerDashboard: React.FC = () => {
  useAuth(); // v3.11.2: was `const { user } = useAuth()` — user was unused. Keep call for context initialization.
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // v1.5.3: farmer grade summary (AI-weighted average of all batch grades)
  const [gradeSummary, setGradeSummary] = useState<any>(null);
  const [gradeHistory, setGradeHistory] = useState<any[]>([]);
  // v1.6.0: harvest notifications + farmer batch verification
  const [harvestNotifications, setHarvestNotifications] = useState<any[]>([]);
  const [harvestForm, setHarvestForm] = useState({ expected_bags: '', expected_weight_kg: '', harvest_date: '', notes: '', farm_id: '' });
  const [harvestLoading, setHarvestLoading] = useState(false);
  const [harvestSuccess, setHarvestSuccess] = useState<string | null>(null);
  const [verifyPin, setVerifyPin] = useState('');
  const [verifyBatchId, setVerifyBatchId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'batches' | 'payments' | 'farms' | 'smart-advisory' | 'pest-scanner' | 'product-scanner' | 'sustainability' | 'advisory' | 'grade-history' | 'harvest' | 'input-credit' | 'quality' | 'yield' | 'activities' | 'ai-assistant' | 'market'>('overview'); // v2.5 + v3.2.6 market
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('MTN');
  const [withdrawPhone, setWithdrawPhone] = useState('');
  const [withdrawStatus, setWithdrawStatus] = useState<string | null>(null);
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await apiClient.get('/farmer/me');
      // ─── Defensive: PostgreSQL NUMERIC fields come through as strings;
      // coerce known monetary/numeric fields to JS numbers to prevent toFixed/toLocaleString crashes.
      const raw = response.data || {};
      const num = (v: any) => (v == null ? 0 : typeof v === 'number' ? v : parseFloat(v));
      setData({
        ...raw,
        available_balance: num(raw.available_balance),
        total_earnings: num(raw.total_earnings),
        total_withdrawn: num(raw.total_withdrawn),
        carbon_points: num(raw.carbon_points),
        carbon_points_value_xaf: num(raw.carbon_points_value_xaf),
        total_batches: parseInt(raw.total_batches) || 0,
        farms: (raw.farms || []).map((f: any) => ({
          ...f,
          polygon_area_hectares: num(f.polygon_area_hectares),
        })),
        farms_summary: raw.farms_summary ? {
          ...raw.farms_summary,
          total_farms: parseInt(raw.farms_summary.total_farms) || 0,
          mapped_farms: parseInt(raw.farms_summary.mapped_farms) || 0,
          eudr_verified_farms: parseInt(raw.farms_summary.eudr_verified_farms) || 0,
          eudr_pending_farms: parseInt(raw.farms_summary.eudr_pending_farms) || 0,
          unmapped_farms: parseInt(raw.farms_summary.unmapped_farms) || 0,
          total_area_ha: num(raw.farms_summary.total_area_ha),
        } : raw.farms_summary,
      });
      setError(null);
    } catch (err: any) {
      setError('Failed to load farmer data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // v1.5.3: fetch the AI-weighted grade summary + grade history (in parallel with main data)
  const fetchGradeData = useCallback(async () => {
    try {
      const [summaryRes, historyRes] = await Promise.all([
        apiClient.get('/farmer/grade-summary').catch(() => ({ data: null })),
        apiClient.get('/farmer/grade-history').catch(() => ({ data: { history: [] } })),
      ]);
      setGradeSummary(summaryRes.data);
      setGradeHistory(historyRes.data?.history || []);
    } catch {
      // Non-fatal — grade data is supplementary
    }
  }, []);

  // v1.6.0: fetch harvest notifications
  const fetchHarvestNotifications = useCallback(async () => {
    try {
      const res = await apiClient.get('/harvest-notifications').catch(() => ({ data: { notifications: [] } }));
      setHarvestNotifications(res.data?.notifications || []);
    } catch {
      // Non-fatal
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchGradeData();
    fetchHarvestNotifications();
    const interval = setInterval(() => { fetchData(); fetchGradeData(); fetchHarvestNotifications(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchData, fetchGradeData, fetchHarvestNotifications]);

  // v1.6.0: submit a harvest notification
  const handleHarvestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHarvestLoading(true);
    setHarvestSuccess(null);
    setError(null);
    try {
      await apiClient.post('/harvest-notifications', {
        farm_id: harvestForm.farm_id || undefined,
        expected_bags: parseInt(harvestForm.expected_bags),
        expected_weight_kg: parseFloat(harvestForm.expected_weight_kg),
        harvest_date: harvestForm.harvest_date || undefined,
        notes: harvestForm.notes || undefined,
      });
      setHarvestSuccess('Harvest notification submitted! A field agent will contact you soon.');
      setHarvestForm({ expected_bags: '', expected_weight_kg: '', harvest_date: '', notes: '', farm_id: '' });
      fetchHarvestNotifications();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit harvest notification');
    } finally {
      setHarvestLoading(false);
    }
  };

  // v1.6.0: farmer verifies a batch with PIN
  const handleVerifyBatch = async (batchId: string) => {
    setError(null);
    setHarvestSuccess(null);
    try {
      const res = await apiClient.post(`/chain/batches/${batchId}/verify`, { pin: verifyPin, method: 'pin' });
      setHarvestSuccess(`Batch verified successfully via PIN. ${res.data?.message || ''}`);
      setVerifyBatchId(null);
      setVerifyPin('');
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Batch verification failed');
    }
  };

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    // v1.6.9: validate withdrawal amount before API call
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      setWithdrawStatus('❌ Please enter a valid amount greater than 0.');
      return;
    }
    if (amount > balance) {
      setWithdrawStatus(`❌ Insufficient balance. You have ${balance.toLocaleString()} XAF available.`);
      return;
    }
    if (!withdrawPhone && withdrawMethod !== 'CASH' && !data?.phone) {
      setWithdrawStatus('❌ Phone number is required for mobile money withdrawals.');
      return;
    }
    setWithdrawLoading(true);
    setWithdrawStatus(null);

    try {
      const response = await apiClient.post('/withdrawals', {
        amount: amount,
        method: withdrawMethod,
        phone: withdrawPhone || data?.phone,
        carrier: withdrawMethod === 'CASH' ? null : withdrawMethod,
      });

      if (response.data.auto_approved) {
        setWithdrawStatus(`✅ ${withdrawMethod} withdrawal of ${amount.toLocaleString()} XAF approved automatically!`);
      } else {
        setWithdrawStatus('⏳ Cash withdrawal submitted. Awaiting financial confirmation.');
      }

      setTimeout(() => {
        setShowWithdrawModal(false);
        setWithdrawStatus(null);
        setWithdrawAmount('');
        fetchData();
      }, 3000);
    } catch (err: any) {
      setWithdrawStatus(err.response?.data?.error || '❌ Withdrawal failed.');
    } finally {
      setWithdrawLoading(false);
    }
  };

  // ─── Pagination hooks (MUST be called unconditionally — before any early return) ───
  const recentBatchesPag = usePagination(data?.recent_batches || [], 10);
  const pendingPaymentsPag = usePagination(data?.pending_payments || [], 10);
  const paymentHistoryPag = usePagination(data?.payment_history || [], 10);
  const withdrawalsPag = usePagination(data?.recent_withdrawals || [], 10);

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="agrarian" />
          <div className="flex items-center gap-2 text-sm text-ink-500 dark:text-ink-400">
            <Spinner size="sm" /> Loading your dashboard…
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center p-5 safe-top">
        <Card padding="lg" className="max-w-md text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-danger-50 dark:bg-danger-500/10 flex items-center justify-center text-danger-600 dark:text-danger-500">
            <Icon.Alert className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Unable to load dashboard</h2>
          <p className="mt-1.5 text-sm text-ink-500 dark:text-ink-400">{error || 'Please try again.'}</p>
          <button onClick={fetchData} className="mt-5 btn-primary">
            <Icon.Refresh className="w-4 h-4" /> Try again
          </button>
        </Card>
      </div>
    );
  }

  const balance = data.available_balance || 0;
  const isKyc = !!data.kyc_verified;
  // Mobile home presentation derives only from live Farmer API/state fields.
  // Prefer a farmer-facing identifier when the API provides one; never invent an ID.
  const farmerDisplayId = String(data.farmer_id || data.farmer_code || data.registration_number || data.phone || data.id || '—');
  const moneyBadgeCount = (data.pending_payments?.length || 0) + (data.recent_withdrawals?.filter((w: any) => !['paid', 'approved', 'completed'].includes(String(w.status || '').toLowerCase())).length || 0);
  const mobileRecentActivities = [
    ...(data.recent_batches || []).map((batch: any) => ({
      key: `batch-${batch.id}`,
      kind: 'batch',
      title: `Batch #${String(batch.batch_code || batch.id || '').slice(0, 12) || '—'}`,
      detail: `${batch.actual_weight || batch.est_weight || '—'} kg${batch.delivered_at ? ` · ${new Date(batch.delivered_at).toLocaleDateString()}` : ''}`,
      status: batch.payment_status || batch.status || 'Recorded',
      tab: 'batches' as const,
      date: batch.delivered_at || batch.updated_at || batch.created_at || '',
    })),
    ...(data.recent_withdrawals || []).map((w: any) => ({
      key: `withdrawal-${w.id}`,
      kind: 'withdrawal',
      title: `Withdrawal${w.carrier ? ` to ${w.carrier}` : ''}`,
      detail: `${Number(w.amount || 0).toLocaleString()} XAF · ${w.method || 'Payment'}`,
      status: w.status || 'Pending',
      tab: 'payments' as const,
      date: w.updated_at || w.created_at || '',
    })),
  ].sort((a: any, b: any) => String(b.date || '').localeCompare(String(a.date || ''))).slice(0, 3);

  const tabs = [
    { id: 'overview',      label: 'Overview',      icon: <Icon.Activity className="w-4 h-4" /> },
    { id: 'batches',       label: 'Batches',       icon: <Icon.Package className="w-4 h-4" />, badge: (data.recent_batches?.length || 0) + (data.pending_payments?.length || 0) || undefined },
    { id: 'payments',      label: 'Payments',      icon: <Icon.Wallet className="w-4 h-4" />, badge: (data.pending_payments?.length || 0) + (data.recent_withdrawals?.length || 0) || undefined },
    { id: 'farms',         label: 'My Farms',      icon: <Icon.Map className="w-4 h-4" />, badge: data.farms?.length || undefined },
    { id: 'smart-advisory',label: 'Smart Advisory',icon: <Icon.Activity className="w-4 h-4" /> },
    { id: 'pest-scanner',  label: 'Pest Scanner',  icon: <Icon.Leaf className="w-4 h-4" /> },
    { id: 'product-scanner', label: 'Product Scan', icon: <Icon.Shield className="w-4 h-4" /> },
    { id: 'sustainability',label: 'Sustainability',icon: <Icon.Leaf className="w-4 h-4" /> },
    { id: 'advisory',      label: 'Advisory',      icon: <Icon.BookOpen className="w-4 h-4" />, badge: data.advisory_content?.length || undefined },
    // v1.6.0: harvest notifications + batch confirmation
    { id: 'harvest',       label: 'Notify Harvest', icon: <Icon.Package className="w-4 h-4" /> },
    { id: 'grade-history', label: 'My Grades',     icon: <Icon.Award className="w-4 h-4" />, badge: gradeSummary?.total_batches || undefined },
    // v1.8.1: cross-portal tabs — input credit + quality results + yield forecast
    { id: 'input-credit', label: 'Input Credit',  icon: <Icon.Package className="w-4 h-4" /> },
    { id: 'quality',      label: 'Quality',       icon: <Icon.Shield className="w-4 h-4" /> },
    { id: 'yield',        label: 'Yield Forecast',icon: <Icon.TrendingUp className="w-4 h-4" /> },
    // v1.8.2: farm activity log (drying, fermentation, harvest tracking)
    { id: 'activities',   label: 'Farm Activities', icon: <Icon.Activity className="w-4 h-4" /> },
    // v2.5: AI Assistant tab — 5 AI agents for the farmer (disease, yield, fertilizer, weather, knowledge)
    { id: 'ai-assistant', label: 'AI Assistant', icon: <Icon.Activity className="w-4 h-4" /> }, // v2.5
    // v3.2.6: Market Dashboard — live cocoa prices, trends, AI market analysis
    { id: 'market',       label: 'Market',       icon: <Icon.TrendingUp className="w-4 h-4" /> }, // v3.2.6
  ];

  // Primary farm for weather/risk widgets on the overview tab
  const primaryFarm = (data?.farms || []).find((f: any) => f.is_primary) || (data?.farms || [])[0] || null;

  return (
    <div className="farmer-dashboard-root min-h-screen bg-ink-50 dark:bg-ink-950 pb-24 md:pb-0">
      {/* v1.6.9: Skip-to-content link for accessibility (WCAG 2.2 AA) */}
      <a href="#farmer-main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-ink-900 focus:text-white focus:rounded-lg">
        Skip to content
      </a>
      {/* ─── Top brand bar ─── */}
      <header className="topbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
          <div className="farmer-topbar-identity flex items-center gap-3 min-w-0">
            <BrandMark size="md" accent="agrarian" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{data.name}</p>
              <p className="text-xs text-ink-500 dark:text-ink-400 truncate">Farmer · HEFARCAM</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {isKyc && (
              <span className="hidden md:inline-flex pill-success">
                <Icon.FileCheck className="w-3 h-3" /> KYC Verified
              </span>
            )}
            <IconButton label="Refresh" onClick={fetchData} disabled={refreshing}>
              <Icon.Refresh className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </IconButton>
            <ProfileButton accent="agrarian" />
            <div className="hidden md:block"><LogoutButton /></div>
          </div>
        </div>
      </header>

      <main id="farmer-main-content" data-mobile-tab={activeTab} className="max-w-7xl mx-auto px-4 md:px-6 pt-6 pb-6">
        {/* ─── Farmer identity banner (always visible) ─── */}
        <Card padding="lg" className="farmer-identity-card hidden md:block mb-6 overflow-hidden relative">
          <div className="absolute -right-12 -top-12 w-48 h-48 brand-surface-agrarian opacity-[0.04] rounded-full" />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              <Avatar name={data.name} size="lg" />
              <div className="min-w-0">
                <p className="section-eyebrow mb-1">Farmer Profile</p>
                <h1 className="text-2xl font-semibold text-ink-900 dark:text-ink-50 tracking-tight truncate">{data.name}</h1>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-ink-500 dark:text-ink-400">
                  <span className="inline-flex items-center gap-1.5"><Icon.Phone className="w-3 h-3" /> {data.phone}</span>
                  <span className="inline-flex items-center gap-1.5"><Icon.MapPin className="w-3 h-3" /> {data.village || 'Unknown'}</span>
                  {isKyc && (
                    <span className="inline-flex items-center gap-1.5 text-success-700 dark:text-success-500">
                      <Icon.Shield className="w-3 h-3" /> KYC verified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* ─── KPI grid ─── */}
        <div className="farmer-kpi-grid hidden md:grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MetricTile
            label="Total batches"
            value={data.total_batches || 0}
            icon={<Icon.Package className="w-4 h-4" />}
            accent="agrarian"
            className="farmer-mobile-metric"
          />
          <MetricTile
            label="Total earnings"
            value={`${(data.total_earnings || 0).toLocaleString()} XAF`}
            icon={<Icon.Dollar className="w-4 h-4" />}
            className="farmer-mobile-metric"
          />
          <MetricTile
            label="Available balance"
            value={`${(balance).toLocaleString()} XAF`}
            icon={<Icon.Wallet className="w-4 h-4" />}
            hint="Ready to withdraw"
            className="farmer-mobile-metric"
          />
          <MetricTile
            label="Avg quality grade"
            value={gradeSummary?.letter_grade || data.average_quality || '—'}
            icon={<Icon.Award className="w-4 h-4" />}
            hint={gradeSummary?.avg_quality_score ? `Score: ${Number(gradeSummary.avg_quality_score).toFixed(1)}/100` : undefined}
            className="farmer-mobile-metric"
          />
        </div>

        {/* ─── Tabs (desktop) ─── */}
        <div className="hidden md:block mb-6">
          <SegmentedTabs items={tabs} active={activeTab} onChange={(id) => setActiveTab(id as any)} />
        </div>

        {/* ─── Mobile tab strip (v1.6.9: with ARIA roles) ─── */}
        <div className="hidden mb-5 -mx-4 px-4 overflow-x-auto no-scrollbar" role="tablist" aria-label="Farmer navigation">
          <div className="inline-flex gap-1.5 pb-1">
            {tabs.map(t => (
              <button
                key={t.id}
                role="tab"
                aria-selected={activeTab === t.id}
                onClick={() => setActiveTab(t.id as any)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  activeTab === t.id
                    ? 'bg-ink-900 dark:bg-ink-50 text-ink-50 dark:text-ink-900'
                    : 'bg-ink-0 dark:bg-ink-850 text-ink-600 dark:text-ink-300 border border-ink-200 dark:border-ink-800'
                }`}
              >
                {t.icon}
                <span>{t.label}</span>
                {t.badge ? <span className="text-2xs opacity-70">({t.badge})</span> : null}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-6">
            <Alert tone="danger">{error}</Alert>
          </div>
        )}

        {activeTab !== 'overview' && (
          <div className="farmer-mobile-page-head-v11 md:hidden">
            <p>FARMER WORKSPACE</p>
            <h1>{tabs.find((tab) => tab.id === activeTab)?.label || 'Farmer workspace'}</h1>
          </div>
        )}

        {/* ═══ OVERVIEW TAB ═══ */}
        {activeTab === 'overview' && (
          <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-overview">
            {/* Mobile-first Farmer Home — same live data/actions, redesigned presentation only. */}
            <div className="farmer-mobile-home-v11 md:hidden" data-testid="farmer-mobile-wallet-home">
              <section className="farmer-mobile-identity-v11" aria-label="Farmer identity">
                <div className="farmer-mobile-avatar-v11" aria-hidden="true">{String(data.name || 'F').charAt(0).toUpperCase()}</div>
                <div className="farmer-mobile-identity-copy-v11">
                  <div className="farmer-mobile-name-row-v11">
                    <h1>{data.name || 'Farmer'}</h1>
                    <span className={`farmer-mobile-verified-v11 ${isKyc ? 'is-verified' : 'is-pending'}`}>
                      <Icon.Shield className="w-3.5 h-3.5" /> {isKyc ? 'Verified farmer' : 'Verification pending'}
                    </span>
                  </div>
                  <div className="farmer-mobile-location-v11"><Icon.MapPin className="w-3.5 h-3.5" /><span>{data.village || 'Location unavailable'}</span></div>
                </div>
                <div className="farmer-mobile-id-v11">
                  <span>FARMER ID ·</span>
                  <strong className="nums" title={farmerDisplayId}>{farmerDisplayId}</strong>
                  <button
                    type="button"
                    className="farmer-mobile-copy-v12"
                    aria-label="Copy farmer ID"
                    onClick={() => navigator.clipboard?.writeText(farmerDisplayId)}
                  ><Icon.Copy /></button>
                </div>
              </section>

              <section className="farmer-mobile-wallet-v11" aria-label="Wallet balance">
                <div className="farmer-mobile-wallet-head-v11"><span>WALLET BALANCE</span><span className="farmer-mobile-currency-v11">XAF</span></div>
                <div className="farmer-mobile-wallet-value-v11 nums">{balance.toLocaleString()} <small>XAF</small></div>
                <p>Ready to withdraw</p>
                <div className="farmer-mobile-payouts-v11" aria-label="Payout methods">
                  <span><i className="is-amber" />MTN/Orange <b>· instant</b></span>
                  <span><i />Cash <b>· admin approval</b></span>
                </div>
                <div className="farmer-mobile-wallet-actions-v11">
                  <button
                    type="button"
                    className="farmer-mobile-primary-v11"
                    disabled={!balance || balance <= 0}
                    onClick={() => { setWithdrawAmount(''); setWithdrawMethod('MTN'); setWithdrawPhone(data?.phone || ''); setShowWithdrawModal(true); }}
                  >Withdraw funds <Icon.ArrowDownRight className="w-4 h-4" /></button>
                  <button type="button" className="farmer-mobile-secondary-v11" onClick={() => setActiveTab('payments')}>Payments</button>
                </div>
              </section>

              <section className="farmer-mobile-kpis-v11" aria-label="Farmer key metrics">
                <button type="button" className="farmer-mobile-kpi-v11" onClick={() => setActiveTab('batches')}>
                  <span className="farmer-mobile-kpi-label-v11">TOTAL BATCHES</span>
                  <span className="farmer-mobile-kpi-icon-wrap-v12"><Icon.Leaf className="farmer-mobile-kpi-icon-v11" /></span>
                  <strong className="nums">{data.total_batches || 0}</strong>
                  <small>— this month</small>
                  <svg className="farmer-mobile-spark-v11" viewBox="0 0 80 28" preserveAspectRatio="none" aria-hidden="true"><path d="M1 23 L16 23 L27 20 L39 21 L50 15 L62 17 L79 12" /></svg>
                </button>
                <button type="button" className="farmer-mobile-kpi-v11" onClick={() => setActiveTab('payments')}>
                  <span className="farmer-mobile-kpi-label-v11">TOTAL EARNINGS — XAF</span>
                  <span className="farmer-mobile-kpi-icon-wrap-v12"><Icon.Dollar className="farmer-mobile-kpi-icon-v11" /></span>
                  <strong className="nums">{Number(data.total_earnings || 0).toLocaleString()} <em>XAF</em></strong>
                  <small>— all earnings</small>
                  <svg className="farmer-mobile-spark-v11" viewBox="0 0 80 28" preserveAspectRatio="none" aria-hidden="true"><path d="M1 23 L22 23 L34 21 L47 22 L59 10 L68 16 L79 17" /></svg>
                </button>
              </section>

              <section className="farmer-mobile-quick-v11" aria-labelledby="farmer-quick-actions-title">
                <h2 id="farmer-quick-actions-title">QUICK ACTIONS</h2>
                <div className="farmer-mobile-quick-grid-v11">
                  <button type="button" onClick={() => setActiveTab('activities')}><span><Icon.Package /></span><strong>Log harvest</strong><small>Record farm activity</small></button>
                  <button type="button" onClick={() => setActiveTab('product-scanner')}><span><Icon.QrCode /></span><strong>Scan QR</strong><small>Verify an input product</small></button>
                  <button type="button" onClick={() => setActiveTab('harvest')}><span><Icon.Truck /></span><strong>Request pickup</strong><small>Notify your harvest</small></button>
                  <button type="button" onClick={() => setActiveTab('smart-advisory')}><span><Icon.BookOpen /></span><strong>Advisory</strong><small>Open your action plan</small></button>
                </div>
              </section>

              <section className="farmer-mobile-activity-v11" aria-labelledby="farmer-recent-activity-title">
                <div className="farmer-mobile-section-head-v11"><h2 id="farmer-recent-activity-title">Recent activity</h2><button type="button" onClick={() => setActiveTab('batches')}>See all</button></div>
                <div className="farmer-mobile-activity-list-v11">
                  {mobileRecentActivities.map((activity: any) => {
                    const normalized = String(activity.status || '').toLowerCase();
                    const success = ['delivered', 'paid', 'approved', 'completed', 'verified', 'passed'].some((value) => normalized.includes(value));
                    const warning = ['pending', 'processing', 'requested', 'in transit', 'in_transit'].some((value) => normalized.includes(value));
                    return (
                      <button type="button" key={activity.key} className="farmer-mobile-activity-row-v11" onClick={() => setActiveTab(activity.tab)}>
                        <span className="farmer-mobile-activity-icon-v11">{activity.kind === 'withdrawal' ? <Icon.Wallet /> : <Icon.Leaf />}</span>
                        <span className="farmer-mobile-activity-copy-v11"><strong>{activity.title}</strong><small className="nums">{activity.detail}</small></span>
                        <span className={`farmer-mobile-status-v11 ${success ? 'is-success' : warning ? 'is-warning' : ''}`}>{activity.status}</span>
                      </button>
                    );
                  })}
                  {!mobileRecentActivities.length && (
                    <button type="button" className="farmer-mobile-empty-hint-v11" onClick={() => setActiveTab('activities')}><Icon.Info className="w-4 h-4" /><span>Log your first harvest to start earning</span><Icon.ArrowUpRight className="w-4 h-4" /></button>
                  )}
                </div>
              </section>
            </div>

            <div className="farmer-desktop-overview hidden md:contents">
            {/* ─── Withdraw CTA ─── */}
            <Card padding="lg" className="farmer-wallet-card mb-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="section-eyebrow mb-1">Wallet</p>
                  <p className="text-3xl font-semibold nums text-ink-900 dark:text-ink-50 tracking-tight">
                    {balance.toLocaleString()} <span className="text-base text-ink-500 dark:text-ink-400">XAF</span>
                  </p>
                  <p className="mt-1.5 text-xs text-ink-500 dark:text-ink-400 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-success-500" /> MTN/Orange — instant</span>
                    <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-warning-500" /> Cash — admin approval</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    setWithdrawAmount('');
                    setWithdrawMethod('MTN');
                    setWithdrawPhone(data?.phone || '');
                    setShowWithdrawModal(true);
                  }}
                  disabled={!balance || balance <= 0}
                  className="btn-accent brand-surface-agrarian shrink-0"
                >
                  <Icon.Wallet className="w-4 h-4" />
                  <span>Withdraw funds</span>
                </button>
              </div>
            </Card>

            {/* ─── v1.5.3: AI-Weighted Grade Summary ─── */}
            {gradeSummary && (
              <Card padding="lg" className="farmer-quality-card mb-6">
                <SectionHeader
                  eyebrow="Quality performance"
                  title="My average grade"
                  subtitle="AI-weighted average across all your batches"
                  icon={<Icon.Award className="w-4 h-4" />}
                />
                <div className="farmer-quality-metrics mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Letter grade + score */}
                  <div className="farmer-quality-stat flex flex-col items-center justify-center p-4 rounded-xl bg-ink-100 dark:bg-ink-800">
                    <div className={`text-4xl font-bold ${
                      gradeSummary.letter_grade === 'A' ? 'text-success-600 dark:text-success-500'
                      : gradeSummary.letter_grade === 'B' ? 'text-warning-600 dark:text-warning-500'
                      : gradeSummary.letter_grade === 'C' ? 'text-danger-600 dark:text-danger-500'
                      : 'text-ink-400'
                    }`}>
                      {gradeSummary.letter_grade || '—'}
                    </div>
                    <p className="text-2xs text-ink-500 mt-1">Average grade</p>
                  </div>
                  {/* Numeric score */}
                  <div className="farmer-quality-stat flex flex-col items-center justify-center p-4 rounded-xl bg-ink-100 dark:bg-ink-800">
                    <div className="text-3xl font-bold nums text-ink-900 dark:text-ink-50">
                      {Number(gradeSummary.avg_quality_score || 0).toFixed(1)}
                    </div>
                    <p className="text-2xs text-ink-500 mt-1">Score / 100</p>
                  </div>
                  {/* Total batches */}
                  <div className="farmer-quality-stat flex flex-col items-center justify-center p-4 rounded-xl bg-ink-100 dark:bg-ink-800">
                    <div className="text-3xl font-bold nums text-ink-900 dark:text-ink-50">
                      {gradeSummary.total_batches || 0}
                    </div>
                    <p className="text-2xs text-ink-500 mt-1">Total batches</p>
                  </div>
                  {/* AI confidence */}
                  <div className="farmer-quality-stat flex flex-col items-center justify-center p-4 rounded-xl bg-ink-100 dark:bg-ink-800">
                    <div className="text-3xl font-bold nums text-ink-900 dark:text-ink-50">
                      {Number(gradeSummary.avg_ai_confidence || 0) > 0
                        ? (Number(gradeSummary.avg_ai_confidence) * 100).toFixed(0) + '%'
                        : '—'}
                    </div>
                    <p className="text-2xs text-ink-500 mt-1">AI confidence</p>
                  </div>
                </div>
                {/* Grade distribution bar */}
                {gradeSummary.total_batches > 0 && (
                  <div className="mt-4">
                    <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-2">Grade distribution</p>
                    <div className="flex h-3 rounded-full overflow-hidden bg-ink-200 dark:bg-ink-700">
                      {gradeSummary.grade_a_count > 0 && (
                        <div className="bg-success-500" style={{ width: `${(gradeSummary.grade_a_count / gradeSummary.total_batches) * 100}%` }} title={`A: ${gradeSummary.grade_a_count}`} />
                      )}
                      {gradeSummary.grade_b_count > 0 && (
                        <div className="bg-warning-500" style={{ width: `${(gradeSummary.grade_b_count / gradeSummary.total_batches) * 100}%` }} title={`B: ${gradeSummary.grade_b_count}`} />
                      )}
                      {gradeSummary.grade_c_count > 0 && (
                        <div className="bg-danger-500" style={{ width: `${(gradeSummary.grade_c_count / gradeSummary.total_batches) * 100}%` }} title={`C: ${gradeSummary.grade_c_count}`} />
                      )}
                    </div>
                    <div className="flex justify-between mt-1.5 text-2xs text-ink-500">
                      <span>A: {gradeSummary.grade_a_count}</span>
                      <span>B: {gradeSummary.grade_b_count}</span>
                      <span>C: {gradeSummary.grade_c_count}</span>
                    </div>
                  </div>
                )}
                {gradeSummary.total_batches > 0 && (
                  <button
                    onClick={() => setActiveTab('grade-history')}
                    className="mt-4 btn-secondary text-xs"
                  >
                    View grade history →
                  </button>
                )}
              </Card>
            )}

            {/* ─── Last delivery ─── */}
            {data.last_delivery && (
              <Card padding="md" className="farmer-last-delivery mb-6">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-lg bg-info-50 dark:bg-info-500/10 text-info-700 dark:text-info-500 flex items-center justify-center">
                    <Icon.Clock className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="section-eyebrow">Last delivery</p>
                    <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 nums">
                      {new Date(data.last_delivery).toLocaleDateString()} at {new Date(data.last_delivery).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* ─── Quick stats summary ─── */}
            <Card padding="lg" className="farmer-activity-card">
              <SectionHeader
                eyebrow="At a glance"
                title="Activity summary"
                subtitle="Recent batches, payments, and compliance at a glance."
                icon={<Icon.Activity className="w-4 h-4" />}
              />
              <div className="farmer-activity-list mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <SummaryRow
                  icon={<Icon.Package className="w-4 h-4" />}
                  label="Recent batches"
                  value={data.recent_batches?.length || 0}
                  hint="Tap Batches tab for details"
                  onClick={() => setActiveTab('batches')}
                />
                <SummaryRow
                  icon={<Icon.Clock className="w-4 h-4" />}
                  label="Pending payments"
                  value={data.pending_payments?.length || 0}
                  hint="Tap Payments tab for details"
                  onClick={() => setActiveTab('payments')}
                />
                <SummaryRow
                  icon={<Icon.Wallet className="w-4 h-4" />}
                  label="Withdrawals"
                  value={data.recent_withdrawals?.length || 0}
                  hint="Tap Payments tab for details"
                  onClick={() => setActiveTab('payments')}
                />
                <SummaryRow
                  icon={<Icon.BookOpen className="w-4 h-4" />}
                  label="Advisory tips"
                  value={data.advisory_content?.length || 0}
                  hint="Tap Advisory tab to read"
                  onClick={() => setActiveTab('advisory')}
                />
              </div>
            </Card>

            {/* ─── v3.2.22: Smart Advisory summary teaser + Weather ───
                v3.2.22 FIX: Removed the duplicate AdvisoryTimelineCard + AgronomistAdvisoryCard
                from the Overview. These are now ONLY shown in the Smart Advisory tab.
                The Overview now shows a clean, enterprise-grade summary card that:
                  - Surfaces today's most urgent action (from the timeline API)
                  - Shows a brief disease risk indicator
                  - Has a clear CTA button to open the full Smart Advisory tab
                This eliminates the visual duplication between Overview and Smart Advisory. */}
            <div className="farmer-overview-conditions-grid grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="farmer-overview-smart-advisory">
                <SmartAdvisorySummaryCard
                farmId={primaryFarm?.id}
                  onOpenFullPlan={() => setActiveTab('smart-advisory')}
                />
              </div>

              <Card padding="lg" className="farmer-overview-weather-card">
                <SectionHeader
                  eyebrow="Local conditions"
                  title="Weather & Disease Risk"
                  subtitle={primaryFarm ? `For ${primaryFarm.name}` : 'Map a farm to enable'}
                  icon={<Icon.MapPin className="w-4 h-4" />}
                />
                <div className="mt-3">
                  {primaryFarm?.centroid_lat && primaryFarm?.centroid_lng ? (
                    <>
                      <WeatherWidget farmId={primaryFarm.id} farmName={primaryFarm.name} />
                      <div className="mt-4 border-t border-ink-200 dark:border-ink-800 pt-4">
                        <DiseaseRiskCard farmId={primaryFarm.id} />
                      </div>
                    </>
                  ) : (
                    <EmptyState
                      icon={<Icon.Map className="w-5 h-5" />}
                      title="No mapped farm"
                      description="Go to My Farms tab to map your farm boundary. Weather + disease risk will appear here."
                    />
                  )}
                </div>
              </Card>
            </div>
            </div>
          </div>
        )}

        {/* ═══ SMART ADVISORY TAB (full timeline — enterprise-grade v3.2.22) ═══ */}
        {activeTab === 'smart-advisory' && (
          <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-smart-advisory space-y-6 farmer-smart-advisory-page">
            {/* v3.2.22: Enterprise-grade hero banner — high-contrast, visible in both light & dark mode */}
            <Card padding="lg" className="farmer-smart-advisory-hero bg-gradient-to-br from-ink-50 to-ink-100 dark:from-ink-900 dark:to-ink-800 border-2 border-ink-200 dark:border-ink-700">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-success-100 dark:bg-success-500/20">
                      <Icon.Activity className="w-4 h-4 text-success-700 dark:text-success-400" />
                    </span>
                    <div>
                      <p className="text-2xs font-semibold uppercase tracking-micro text-success-700 dark:text-success-400">AI-Powered · Weather-Driven · KB-Grounded</p>
                      <h2 className="text-xl font-bold text-ink-900 dark:text-ink-50">Your 7-Day Farming Action Plan</h2>
                    </div>
                  </div>
                  <p className="text-sm text-ink-700 dark:text-ink-200 leading-relaxed max-w-2xl">
                    Generated by Gemini using live weather data, disease risk assessment, and the Cameroon Cocoa Digital Twin knowledge base. Each action is prioritized, time-windowed, and weather-aware.
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400">Powered by</span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-ink-900 dark:bg-ink-100 text-ink-50 dark:text-ink-900 text-xs font-semibold">
                    <Icon.Sparkles className="w-3 h-3" /> Enterprise AI
                  </span>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 farmer-smart-advisory-layout">
              <div className="lg:col-span-2 space-y-4 farmer-smart-advisory-primary">
                {/* v3.2.22: Primary timeline card — high-contrast wrapper */}
                <Card padding="lg" className="farmer-smart-advisory-timeline border-l-4 border-l-success-500">
                  <SectionHeader
                    className="farmer-smart-advisory-timeline-heading"
                    eyebrow="Today's priorities + 7-day outlook"
                    title="Action Timeline"
                    subtitle="Tap ✓ Done to confirm completed actions. The AI learns from your feedback."
                    icon={<Icon.Activity className="w-4 h-4" />}
                  />
                  <div className="mt-4">
                    <AdvisoryTimelineCard />
                  </div>
                </Card>

                {/* v3.2.22: AI Agronomist fallback — enterprise-styled */}
                <Card padding="lg" className="farmer-smart-advisory-agronomist border-l-4 border-l-info-500">
                  <AgronomistAdvisoryCard farm={primaryFarm} />
                </Card>
              </div>

              {/* Right sidebar — weather + disease risk */}
              <div className="space-y-4 farmer-smart-advisory-context">
                <Card padding="lg" className="farmer-smart-advisory-weather">
                  <SectionHeader
                    eyebrow="Right now"
                    title="Weather"
                    icon={<Icon.MapPin className="w-4 h-4" />}
                  />
                  <div className="mt-3">
                    {primaryFarm ? (
                      <WeatherWidget farmId={primaryFarm.id} farmName={primaryFarm.name} />
                    ) : (
                      <EmptyState icon={<Icon.Map className="w-5 h-5" />} title="No mapped farm" description="Map your farm first." />
                    )}
                  </div>
                </Card>
                <Card padding="lg" className="farmer-smart-advisory-risk">
                  <SectionHeader
                    eyebrow="Today"
                    title="Disease Risk"
                    icon={<Icon.Shield className="w-4 h-4" />}
                  />
                  <div className="mt-3">
                    {primaryFarm ? (
                      <DiseaseRiskCard farmId={primaryFarm.id} />
                    ) : (
                      <EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No mapped farm" description="Map your farm first." />
                    )}
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* ═══ BATCHES TAB ═══ */}
        {activeTab === 'batches' && (
          <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-batches space-y-6">
            {/* ─── Batch history ─── */}
            {recentBatchesPag.total > 0 ? (
              <Card padding="none" className="overflow-hidden farmer-record-section farmer-batches-section">
                <div className="p-5 border-b border-ink-200 dark:border-ink-800 farmer-record-section-head">
                  <SectionHeader
                    eyebrow="Recent activity"
                    title={`Recent batches (${recentBatchesPag.total})`}
                    icon={<Icon.Package className="w-4 h-4" />}
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table mobile-record-table farmer-batches-records">
                    <thead>
                      <tr><th>Batch</th><th>Weight</th><th>Grade</th><th>Score</th><th>Verified</th><th>Amount</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {recentBatchesPag.paginatedItems.map((batch: any) => (
                        <tr key={batch.id}>
                          <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{batch.id?.slice(0, 8)}…</td>
                          <td className="nums">{batch.actual_weight || batch.est_weight || '—'} kg</td>
                          <td><StatusPill tone={batch.quality_grade === 'A' ? 'success' : batch.quality_grade === 'B' ? 'warning' : 'danger'} dot>{batch.quality_grade || '—'}</StatusPill></td>
                          <td className="nums">{batch.quality_score ? Number(batch.quality_score).toFixed(0) : '—'}</td>
                          <td>{batch.farmer_verified ? <span className="text-success-600 dark:text-success-500 text-xs">✓</span> : <span className="text-ink-400 text-xs">—</span>}</td>
                          <td className="nums font-medium text-ink-900 dark:text-ink-50">
                            {batch.calculated_amount ? `${Number(batch.calculated_amount).toLocaleString()} XAF` : '—'}
                          </td>
                          <td><SmartStatus status={batch.payment_status || batch.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-ink-200 dark:border-ink-800">
                  <Pagination
                    page={recentBatchesPag.page}
                    pageSize={recentBatchesPag.pageSize}
                    total={recentBatchesPag.total}
                    onPageChange={recentBatchesPag.setPage}
                  />
                </div>
              </Card>
            ) : (
              <Card padding="lg">
                <EmptyState
                  icon={<Icon.Package className="w-5 h-5" />}
                  title="No batches yet"
                  description="Your recent cocoa deliveries will appear here once recorded."
                />
              </Card>
            )}
          </div>
        )}

        {/* ═══ PAYMENTS TAB ═══ */}
        {activeTab === 'payments' && (
          <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-payments space-y-6">
            {/* ─── Withdraw CTA (also on payments tab for convenience) ─── */}
            <Card padding="lg" className="farmer-payments-wallet-card">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 farmer-payments-wallet-layout">
                <div>
                  <p className="section-eyebrow mb-1">Wallet</p>
                  <p className="text-3xl font-semibold nums text-ink-900 dark:text-ink-50 tracking-tight">
                    {balance.toLocaleString()} <span className="text-base text-ink-500 dark:text-ink-400">XAF</span>
                  </p>
                  <p className="mt-1.5 text-xs text-ink-500 dark:text-ink-400 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-success-500" /> MTN/Orange — instant</span>
                    <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-warning-500" /> Cash — admin approval</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    setWithdrawAmount('');
                    setWithdrawMethod('MTN');
                    setWithdrawPhone(data?.phone || '');
                    setShowWithdrawModal(true);
                  }}
                  disabled={!balance || balance <= 0}
                  className="btn-accent brand-surface-agrarian shrink-0"
                >
                  <Icon.Wallet className="w-4 h-4" />
                  <span>Withdraw funds</span>
                </button>
              </div>
            </Card>

            {/* ─── Pending payments ─── */}
            {pendingPaymentsPag.total > 0 && (
              <Card padding="none" className="overflow-hidden farmer-record-section farmer-payments-pending-card">
                <div className="p-5 border-b border-ink-200 dark:border-ink-800 farmer-record-section-head">
                  <SectionHeader
                    eyebrow="Awaiting payout"
                    title={`Pending payments (${pendingPaymentsPag.total})`}
                    icon={<Icon.Clock className="w-4 h-4" />}
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table mobile-record-table farmer-payments-pending-records">
                    <thead>
                      <tr><th>Batch</th><th>Weight</th><th>Quality</th><th>Amount</th></tr>
                    </thead>
                    <tbody>
                      {pendingPaymentsPag.paginatedItems.map((batch: any) => (
                        <tr key={batch.id}>
                          <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{batch.id?.slice(0, 8)}…</td>
                          <td className="nums">{batch.actual_weight || batch.est_weight || '—'} kg</td>
                          <td><SmartStatus status={batch.quality_grade} /></td>
                          <td className="nums font-medium text-ink-900 dark:text-ink-50">
                            {batch.calculated_amount ? `${Number(batch.calculated_amount).toLocaleString()} XAF` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-ink-200 dark:border-ink-800">
                  <Pagination
                    page={pendingPaymentsPag.page}
                    pageSize={pendingPaymentsPag.pageSize}
                    total={pendingPaymentsPag.total}
                    onPageChange={pendingPaymentsPag.setPage}
                  />
                </div>
              </Card>
            )}

            {/* ─── Payment history ─── */}
            {paymentHistoryPag.total > 0 ? (
              <Card padding="none" className="overflow-hidden farmer-record-section farmer-payments-history-card">
                <div className="p-5 border-b border-ink-200 dark:border-ink-800 farmer-record-section-head">
                  <SectionHeader
                    eyebrow="Settled"
                    title={`Payment history (${paymentHistoryPag.total})`}
                    icon={<Icon.Check className="w-4 h-4" />}
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table mobile-record-table farmer-payments-history-records">
                    <thead>
                      <tr><th>Batch</th><th>Date</th><th>Weight</th><th>Quality</th><th>Amount</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {paymentHistoryPag.paginatedItems.map((batch: any) => (
                        <tr key={batch.id}>
                          <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{batch.id?.slice(0, 8)}…</td>
                          <td className="text-2xs text-ink-500 dark:text-ink-400 nums">
                            {batch.delivered_at ? new Date(batch.delivered_at).toLocaleDateString() : batch.collected_at ? new Date(batch.collected_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="nums">{batch.actual_weight || batch.est_weight || '—'} kg</td>
                          <td><SmartStatus status={batch.quality_grade} /></td>
                          <td className="nums font-medium text-ink-900 dark:text-ink-50">
                            {batch.calculated_amount ? `${Number(batch.calculated_amount).toLocaleString()} XAF` : '—'}
                          </td>
                          <td><SmartStatus status={batch.payment_status || 'PAID'} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-ink-200 dark:border-ink-800">
                  <Pagination
                    page={paymentHistoryPag.page}
                    pageSize={paymentHistoryPag.pageSize}
                    total={paymentHistoryPag.total}
                    onPageChange={paymentHistoryPag.setPage}
                  />
                </div>
              </Card>
            ) : (
              <Card padding="lg">
                <EmptyState
                  icon={<Icon.Check className="w-5 h-5" />}
                  title="No payment history yet"
                  description="Your settled payments will appear here once batches are paid out."
                />
              </Card>
            )}

            {/* ─── Withdrawal history ─── */}
            {withdrawalsPag.total > 0 ? (
              <Card padding="none" className="overflow-hidden farmer-record-section farmer-withdrawal-history-card">
                <div className="p-5 border-b border-ink-200 dark:border-ink-800 farmer-record-section-head">
                  <SectionHeader
                    eyebrow="Cash out"
                    title="Withdrawal history"
                    icon={<Icon.History className="w-4 h-4" />}
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table mobile-record-table farmer-withdrawal-records">
                    <thead>
                      <tr><th>Reference</th><th>Amount</th><th>Method</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {withdrawalsPag.paginatedItems.map((w: any) => (
                        <tr key={w.id}>
                          <td className="font-mono text-2xs text-ink-500 dark:text-ink-400">{w.reference?.slice(0, 12)}…</td>
                          <td className="nums font-medium text-ink-900 dark:text-ink-50">{Number(w.amount).toLocaleString()} XAF</td>
                          <td className="text-2xs text-ink-500 dark:text-ink-400">{w.method}{w.carrier ? ` · ${w.carrier}` : ''}</td>
                          <td><SmartStatus status={w.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-ink-200 dark:border-ink-800">
                  <Pagination
                    page={withdrawalsPag.page}
                    pageSize={withdrawalsPag.pageSize}
                    total={withdrawalsPag.total}
                    onPageChange={withdrawalsPag.setPage}
                  />
                </div>
              </Card>
            ) : (
              <Card padding="lg">
                <EmptyState
                  icon={<Icon.History className="w-5 h-5" />}
                  title="No withdrawals yet"
                  description="Your withdrawal requests will appear here."
                />
              </Card>
            )}
          </div>
        )}

        {/* ═══ FARMS TAB (multi-farm per farmer) ═══ */}
        {activeTab === 'farms' && (
          <MyFarmsTab farmerId={data.id} farms={data.farms || []} farmsSummary={data.farms_summary} onRefresh={fetchData} />
        )}

        {/* ═══ PEST SCANNER TAB ═══ */}
        {activeTab === 'pest-scanner' && <PestScannerTab farmerId={data.id} />}

        {/* ═══ PRODUCT SCANNER TAB (CC-DTIP v4.0 Phase 19.6) ═══ */}
        {activeTab === 'product-scanner' && (
          <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-product-scanner"><ProductScanner farmerId={data.id} /></div>
        )}

        {/* ═══ SUSTAINABILITY TAB ═══ */}
        {activeTab === 'sustainability' && (
          <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-sustainability space-y-6">
            {/* v1.9.4: Sustainability points + tier from farm activities */}
            <FarmerSustainabilitySummary farmerId={data.id} />
            <CarbonCreditSection farmerId={data.id} />
            <EudrStatusSection farmerId={data.id} />
          </div>
        )}

        {/* ═══ ADVISORY TAB ═══ */}
        {activeTab === 'advisory' && (
          <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-advisory space-y-6">
            {data.advisory_content && data.advisory_content.length > 0 ? (
              <Card padding="none" className="overflow-hidden farmer-advisory-feed-card">
                <div className="p-5 border-b border-ink-200 dark:border-ink-800 farmer-record-section-head">
                  <SectionHeader
                    eyebrow="Knowledge base"
                    title="Advisory tips"
                    subtitle="Latest recommendations from HEFARCAM agronomists."
                    icon={<Icon.BookOpen className="w-4 h-4" />}
                  />
                </div>
                <div className="p-5 space-y-2.5">
                  {data.advisory_content.map((item: any) => (
                    <div key={item.id} className="p-3.5 rounded-lg surface-inset farmer-advisory-item">
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{item.title}</h4>
                        <span className="pill-neutral capitalize shrink-0">{item.category}</span>
                      </div>
                      <p className="text-sm text-ink-600 dark:text-ink-300 text-pretty">{item.content}</p>
                    </div>
                  ))}
                </div>
              </Card>
            ) : (
              <Card padding="lg">
                <EmptyState
                  icon={<Icon.BookOpen className="w-5 h-5" />}
                  title="No advisory content"
                  description="Tips from HEFARCAM agronomists will appear here when published."
                />
              </Card>
            )}
          </div>
        )}

        {/* ═══ v1.8.1: Cross-portal tabs — Input Credit + Quality + Yield Forecast ═══ */}

        {/* ─── Input Credit Tab (farmer sees their input debt + repayment) ─── */}
        {activeTab === 'input-credit' && <FarmerInputCreditTab farmerId={data.id} />}

        {/* ─── Quality Tab (farmer sees lab tests + organoleptic for their batches) ─── */}
        {activeTab === 'quality' && <FarmerQualityTab farmerId={data.id} />}

        {/* ─── Yield Forecast Tab (farmer sees their farm's biological capacity) ─── */}
        {activeTab === 'yield' && <FarmerYieldTab farmerId={data.id} farms={data.farms || []} />}

        {/* ─── v1.8.2: Farm Activities Tab (record harvest, fermentation, drying, bagging) ─── */}
        {activeTab === 'activities' && <FarmerActivitiesTab farmerId={data.id} farms={data.farms || []} />}

        {/* ─── v2.5: AI Assistant Tab (5 AI agents: disease, yield, fertilizer, weather, knowledge) ─── */}
        {activeTab === 'ai-assistant' && <FarmerAIAssistantTab farmerId={data.id} farms={data.farms || []} villageName={data.village || ''} />} {/* v2.5 */}

        {/* ─── v3.2.6: Market Dashboard Tab (live cocoa prices + AI market analysis) ─── */}
        {activeTab === 'market' && <FarmerMarketTab villageName={data.village || ''} />} {/* v3.2.6 */}

        {/* ─── v1.6.0: Harvest Notification Tab ─── */}
        {activeTab === 'harvest' && (
          <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-harvest space-y-6">
            {/* Submit new harvest notification */}
            <Card padding="lg" className="farmer-harvest-form-card farmer-section-card">
              <SectionHeader
                eyebrow="Phase 3 — Harvest Notification"
                title="Notify Harvest Ready"
                subtitle="Tell the cooperative your cocoa is ready for pickup. A field agent will be dispatched to inspect."
                icon={<Icon.Package className="w-4 h-4" />}
              />
              {harvestSuccess && <Alert tone="success" onClose={() => setHarvestSuccess(null)}>{harvestSuccess}</Alert>}
              <form onSubmit={handleHarvestSubmit} className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="h-bags" className="label">Expected bags *</label>
                    <input id="h-bags" type="number" min="1" required value={harvestForm.expected_bags}
                      onChange={(e) => setHarvestForm({ ...harvestForm, expected_bags: e.target.value })}
                      className="input" placeholder="e.g. 18" />
                  </div>
                  <div>
                    <label htmlFor="h-weight" className="label">Expected weight (kg) *</label>
                    <input id="h-weight" type="number" step="0.1" min="0" required value={harvestForm.expected_weight_kg}
                      onChange={(e) => setHarvestForm({ ...harvestForm, expected_weight_kg: e.target.value })}
                      className="input" placeholder="e.g. 1120" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="h-date" className="label">Harvest date</label>
                    <input id="h-date" type="date" value={harvestForm.harvest_date}
                      onChange={(e) => setHarvestForm({ ...harvestForm, harvest_date: e.target.value })}
                      className="input" />
                  </div>
                  <div>
                    <label htmlFor="h-farm" className="label">Farm</label>
                    <select id="h-farm" value={harvestForm.farm_id}
                      onChange={(e) => setHarvestForm({ ...harvestForm, farm_id: e.target.value })}
                      className="input">
                      <option value="">— Select farm —</option>
                      {(data?.farms || []).map((f: any) => (
                        <option key={f.id} value={f.id}>{f.name || f.village || 'Farm'} ({Number(f.polygon_area_hectares || 0).toFixed(1)} ha)</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="h-notes" className="label">Notes</label>
                  <textarea id="h-notes" value={harvestForm.notes}
                    onChange={(e) => setHarvestForm({ ...harvestForm, notes: e.target.value })}
                    className="input" rows={2} placeholder="e.g. cocoa is in the drying shed" />
                </div>
                <button type="submit" disabled={harvestLoading} className="btn-accent w-full brand-surface-terrain">
                  {harvestLoading ? <><Spinner size="sm" /> Submitting…</> : <><Icon.Package className="w-4 h-4" /> Submit Harvest Notification</>}
                </button>
              </form>
            </Card>

            {/* List of farmer's harvest notifications */}
            <Card padding="none" className="overflow-hidden farmer-harvest-history-card">
              <div className="p-5 border-b border-ink-200 dark:border-ink-800 farmer-record-section-head">
                <SectionHeader eyebrow="My notifications" title="Harvest History" subtitle="Status of your past harvest notifications" />
              </div>
              {harvestNotifications.length === 0 ? (
                <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No notifications yet" description="Submit a harvest notification above to alert the cooperative." /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table mobile-record-table farmer-harvest-records">
                    <thead><tr><th>Notification #</th><th>Bags</th><th>Weight (kg)</th><th>Status</th><th>Submitted</th></tr></thead>
                    <tbody>
                      {harvestNotifications.map((n: any) => (
                        <tr key={n.id}>
                          <td className="text-xs font-mono">{n.notification_number || n.id.slice(0, 8)}</td>
                          <td className="nums">{n.expected_bags}</td>
                          <td className="nums">{Number(n.expected_weight_kg || 0).toLocaleString()}</td>
                          <td><SmartStatus status={n.status} /></td>
                          <td className="text-2xs">{n.created_at ? new Date(n.created_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Batch Verification section */}
            {/* v2.1.1 (BUG#2 fix): server returns `recent_batches` (not `batches`).
                Previously this section never rendered because `data?.batches` was always undefined,
                so farmers could never PIN-verify their batches. */}
            {data?.recent_batches && data.recent_batches.filter((b: any) => !b.farmer_verified).length > 0 && (
              <Card padding="lg" className="farmer-harvest-verify-card">
                <SectionHeader
                  eyebrow="Phase 5 — Batch Verification"
                  title="Confirm Your Batches"
                  subtitle="Verify the weight and quantity of your batches to prevent disputes"
                  icon={<Icon.Check className="w-4 h-4" />}
                />
                <div className="mt-4 space-y-2">
                  {data.recent_batches.filter((b: any) => !b.farmer_verified).map((b: any) => (
                    <div key={b.id} className="farmer-batch-verify-row flex items-center justify-between p-3 rounded-lg surface-inset">
                      <div>
                        <p className="text-sm font-medium">Batch {b.id.slice(0, 8)}…</p>
                        <p className="text-2xs text-ink-500">{b.actual_weight || b.est_weight} kg · Grade {b.quality_grade || '—'}</p>
                      </div>
                      {verifyBatchId === b.id ? (
                        <div className="flex gap-2 items-center">
                          <input type="password" maxLength={4} placeholder="PIN" value={verifyPin}
                            onChange={(e) => setVerifyPin(e.target.value)} className="input w-20 text-center" />
                          <button onClick={() => handleVerifyBatch(b.id)} disabled={!/^\d{4}$/.test(verifyPin)}
                            className="btn-primary text-xs">Verify</button>
                          <button onClick={() => { setVerifyBatchId(null); setVerifyPin(''); }} className="btn-secondary text-xs">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setVerifyBatchId(b.id)} className="btn-secondary text-xs">
                          <Icon.Check className="w-3 h-3" /> Verify with PIN
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* ─── v1.5.3: Grade History Tab ─── */}
        {activeTab === 'grade-history' && (
          <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-grade-history space-y-6">
            {gradeSummary && (
              <Card padding="lg" className="farmer-grade-summary-card">
                <SectionHeader
                  eyebrow="Quality performance"
                  title="Grade history & trends"
                  subtitle={`${gradeSummary.total_batches || 0} batches graded · Average score: ${Number(gradeSummary.avg_quality_score || 0).toFixed(1)}/100 (${gradeSummary.letter_grade || '—'})`}
                  icon={<Icon.Award className="w-4 h-4" />}
                />
              </Card>
            )}

            {gradeHistory.length === 0 ? (
              <Card padding="lg">
                <EmptyState
                  icon={<Icon.Award className="w-5 h-5" />}
                  title="No graded batches yet"
                  description="Your batch grades will appear here once your cocoa is delivered and graded."
                />
              </Card>
            ) : (
              <Card padding="none" className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="data-table mobile-record-table farmer-grade-records">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Grade</th>
                        <th>Score</th>
                        <th>AI Confidence</th>
                        <th>Method</th>
                        <th>Weight (kg)</th>
                        <th>Amount (XAF)</th>
                        <th>Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gradeHistory.map((b: any, i: number) => (
                        <tr key={b.id || i}>
                          <td className="text-2xs text-ink-500">
                            {b.collected_at ? new Date(b.collected_at).toLocaleDateString() : '—'}
                          </td>
                          <td>
                            <StatusPill
                              tone={b.quality_grade === 'A' ? 'success' : b.quality_grade === 'B' ? 'warning' : 'danger'}
                              dot
                            >
                              {b.quality_grade}
                            </StatusPill>
                          </td>
                          <td className="nums font-medium">{Number(b.quality_score || 0).toFixed(1)}</td>
                          <td className="nums text-2xs">
                            {b.ai_grade_confidence != null
                              ? (Number(b.ai_grade_confidence) * 100).toFixed(0) + '%'
                              : '—'}
                          </td>
                          <td className="text-2xs">
                            <StatusPill tone={b.grading_method === 'auto' ? 'info' : 'neutral'}>
                              {b.grading_method || 'deterministic'}
                            </StatusPill>
                          </td>
                          <td className="nums">{Number(b.weight_kg || 0).toFixed(1)}</td>
                          <td className="nums">{Number(b.calculated_amount || 0).toLocaleString()}</td>
                          <td><SmartStatus status={b.payment_status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* ─── Withdraw modal (desktop) / sheet (mobile) ─── */}
      {showWithdrawModal && (
        <>
          <div className="sheet-backdrop hidden md:block" onClick={() => setShowWithdrawModal(false)} />
          <div className="hidden md:flex fixed inset-0 z-50 items-center justify-center p-6 pointer-events-none">
            <Card padding="lg" className="max-w-md w-full pointer-events-auto animate-scale-in">
              <WithdrawForm
                data={data}
                amount={withdrawAmount} setAmount={setWithdrawAmount}
                method={withdrawMethod} setMethod={setWithdrawMethod}
                phone={withdrawPhone} setPhone={setWithdrawPhone}
                status={withdrawStatus} loading={withdrawLoading}
                onSubmit={handleWithdraw}
                onClose={() => setShowWithdrawModal(false)}
              />
            </Card>
          </div>
          {/* Mobile bottom sheet */}
          <div className="md:hidden">
            <div className="sheet-backdrop" onClick={() => setShowWithdrawModal(false)} />
            <div className="sheet animate-sheet-up">
              <div className="sheet-handle" />
              <div className="px-5 pb-4 pt-2 flex items-center justify-between">
                <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">Withdraw funds</h3>
                <IconButton label="Close" onClick={() => setShowWithdrawModal(false)} size="sm">
                  <Icon.X className="w-4 h-4" />
                </IconButton>
              </div>
              <div className="px-5 pb-6">
                <WithdrawForm
                  data={data}
                  amount={withdrawAmount} setAmount={setWithdrawAmount}
                  method={withdrawMethod} setMethod={setWithdrawMethod}
                  phone={withdrawPhone} setPhone={setWithdrawPhone}
                  status={withdrawStatus} loading={withdrawLoading}
                  onSubmit={handleWithdraw}
                  onClose={() => setShowWithdrawModal(false)}
                  embedded
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Farmer-first mobile bottom navigation — core journeys only; full 17-tab navigation remains in the audited workspace drawer. */}
      <nav className="farmer-mobile-bottom-nav md:hidden" aria-label="Farmer primary navigation">
        {[
          { id: 'overview', label: 'Home', icon: <Icon.Home className="w-5 h-5" /> },
          { id: 'batches', label: 'Batches', icon: <Icon.Package className="w-5 h-5" /> },
          { id: 'payments', label: 'Money', icon: <Icon.Wallet className="w-5 h-5" /> },
          { id: 'farms', label: 'Farms', icon: <Icon.Map className="w-5 h-5" /> },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            className={`farmer-mobile-bottom-tab ${activeTab === item.id ? 'is-active' : ''}`}
            aria-current={activeTab === item.id ? 'page' : undefined}
            onClick={() => { setActiveTab(item.id as any); requestAnimationFrame(() => document.getElementById('farmer-main-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' })); }}
          >
            <span className="farmer-mobile-bottom-icon">{item.icon}{item.id === 'payments' && moneyBadgeCount > 0 ? <span className="farmer-mobile-money-badge-v12 nums">{moneyBadgeCount > 9 ? '9+' : moneyBadgeCount}</span> : null}</span>
            <span>{item.label}</span>
          </button>
        ))}
        <button
          type="button"
          className={`farmer-mobile-bottom-tab ${!['overview','batches','payments','farms'].includes(activeTab) ? 'is-active' : ''}`}
          aria-label="Open all farmer tools"
          onClick={() => document.querySelector<HTMLButtonElement>('.ed-mobile-workspace-trigger')?.click()}
        >
          <span className="farmer-mobile-bottom-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true"><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></svg>
          </span>
          <span>More</span>
        </button>
      </nav>

      <CopilotWidget userRole="farmer" />
</div>
  );
};

/* ─── Withdraw form (shared between modal & sheet) ─── */
const WithdrawForm: React.FC<{
  data: any;
  amount: string; setAmount: (v: string) => void;
  method: string; setMethod: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  status: string | null; loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  embedded?: boolean;
}> = ({ data, amount, setAmount, method, setMethod, phone, setPhone, status, loading, onSubmit, onClose, embedded }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div className="p-3.5 rounded-lg surface-inset">
      <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">Available balance</p>
      <p className="text-2xl font-semibold nums text-ink-900 dark:text-ink-50 mt-0.5">
        {(data?.available_balance || 0).toLocaleString()} <span className="text-sm text-ink-500 dark:text-ink-400">XAF</span>
      </p>
      <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">
        MTN/Orange — instant · Cash — admin approval
      </p>
    </div>

    <div>
      <label htmlFor="w-amount" className="label">Amount (XAF)</label>
      <input
        id="w-amount"
        type="number"
        required
        max={data?.available_balance || 0}
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        className="input"
        placeholder="Enter amount"
      />
    </div>

    <div>
      <label htmlFor="w-method" className="label">Method</label>
      <select
        id="w-method"
        value={method}
        onChange={(e) => setMethod(e.target.value)}
        className="input"
      >
        <option value="MTN">MTN Mobile Money — instant</option>
        <option value="Orange">Orange Money — instant</option>
        <option value="CASH">Cash — pending confirmation</option>
      </select>
    </div>

    {method !== 'CASH' && (
      <div>
        <label htmlFor="w-phone" className="label">Phone number</label>
        <input
          id="w-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="input"
          placeholder="+237 6XX XXX XXX"
        />
      </div>
    )}

    {method === 'CASH' && (
      <Alert tone="info">
        Cash withdrawals require confirmation from the finance team. You'll be notified once approved.
      </Alert>
    )}

    {status && (
      <Alert tone={status.includes('✅') || status.includes('auto') ? 'success' : status.includes('⏳') ? 'info' : 'danger'}>
        {status.replace(/[✅⏳❌]/g, '').trim()}
      </Alert>
    )}

    <button
      type="submit"
      disabled={loading}
      className="btn-accent w-full py-3 brand-surface-agrarian"
    >
      {loading ? (
        <><Spinner size="sm" /> <span>Processing…</span></>
      ) : (
        <><Icon.Send className="w-4 h-4" /> <span>Request withdrawal</span></>
      )}
    </button>
  </form>
);

/* ═══════════════════════════════════════════════════════════════
 * My Farms Tab — multi-farm per farmer with EUDR status per farm
 * ═══════════════════════════════════════════════════════════════ */
interface FarmShape {
  id: string;
  name: string;
  village: string | null;
  cooperative: string | null;
  gps_polygon: any;
  polygon_area_hectares: number;
  centroid_lat: number | null;
  centroid_lng: number | null;
  primary_crop: string | null;
  estimated_trees: number | null;
  soil_type: string | null;
  altitude_m: number | null;
  eudr_status: 'UNMAPPED' | 'PENDING' | 'VERIFIED' | 'REJECTED' | 'EXPIRED';
  eudr_risk_level: string | null;
  eudr_verified_at: string | null;
  eudr_expires_at: string | null;
  is_primary: boolean;
  is_active: boolean;
  notes: string | null;
  created_at: string;
}

const EUDR_BADGE: Record<string, { tone: 'success' | 'warning' | 'danger' | 'info'; label: string }> = {
  VERIFIED: { tone: 'success', label: 'EUDR Verified' },
  PENDING:  { tone: 'warning', label: 'EUDR Pending' },
  UNMAPPED: { tone: 'info',    label: 'Unmapped' },
  REJECTED: { tone: 'danger',  label: 'EUDR Rejected' },
  EXPIRED:  { tone: 'danger',  label: 'EUDR Expired' },
};

const MyFarmsTab: React.FC<{
  farmerId: string;
  farms: FarmShape[];
  farmsSummary: any;
  onRefresh: () => void;
}> = ({ farmerId, farms, farmsSummary, onRefresh }) => {
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [regulatoryPlots, setRegulatoryPlots] = useState<any[]>([]);
  const [plotsLoading, setPlotsLoading] = useState(true);
  useEffect(() => {
    let active = true;
    apiClient.get('/mapping/farmer/my-plots').then(r => { if (active) setRegulatoryPlots(r.data.plots || []); }).catch(() => { if (active) setRegulatoryPlots([]); }).finally(() => { if (active) setPlotsLoading(false); });
    return () => { active = false; };
  }, [farms]);

  const handleSetPrimary = async (farmId: string) => {
    setActionError(null); setActionSuccess(null);
    try {
      await apiClient.post(`/farms/${farmId}/set-primary`);
      setActionSuccess('Primary farm updated.');
      onRefresh();
      setTimeout(() => setActionSuccess(null), 2500);
    } catch (err: any) {
      setActionError(err.response?.data?.error || 'Failed to set primary farm');
    }
  };



  const summary = farmsSummary || {
    total_farms: farms.length,
    mapped_farms: farms.filter(f => f.gps_polygon).length,
    eudr_verified_farms: farms.filter(f => f.eudr_status === 'VERIFIED').length,
    eudr_pending_farms: farms.filter(f => f.eudr_status === 'PENDING').length,
    unmapped_farms: farms.filter(f => f.eudr_status === 'UNMAPPED').length,
    total_area_ha: farms.reduce((s, f) => s + (f.polygon_area_hectares || 0), 0),
  };

  return (
    <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-farms space-y-6">
      {actionError && <Alert tone="danger" onClose={() => setActionError(null)}>{actionError}</Alert>}
      {actionSuccess && <Alert tone="success">{actionSuccess}</Alert>}

      {/* ─── Farms summary KPIs ─── */}
      <div className="farmer-farms-kpi-grid grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total farms"      value={summary.total_farms || 0}        icon={<Icon.Layers className="w-4 h-4" />} accent="agrarian" />
        <MetricTile label="Mapped farms"     value={summary.mapped_farms || 0}       icon={<Icon.Map className="w-4 h-4" />} />
        <MetricTile label="EUDR verified"    value={summary.eudr_verified_farms || 0} icon={<Icon.Shield className="w-4 h-4" />} />
        <MetricTile label="Total area"       value={`${Number(summary.total_area_ha || 0).toFixed(2)} ha`} icon={<Icon.MapPin className="w-4 h-4" />} />
      </div>

      {/* ─── Add farm button ─── */}
      {/* v1.7.6: Farmers can NO LONGER add farms themselves. Only field agents can add farms.
          The farmer can still view their farms and edit existing ones (if permitted by backend),
          but the "Add farm" button has been removed per business rule: farms are registered by
          field agents during farmer onboarding or via the Field Agent portal's farm management. */}
      <Card padding="lg" className="farmer-farms-info-card farmer-section-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="section-eyebrow mb-1">My farms</p>
            <p className="text-sm text-ink-700 dark:text-ink-300">
              Approved farm boundaries are read-only here. Regulatory geometry is created and versioned through the field-agent evidence workflow.
            </p>
          </div>
          {/* v1.7.6: removed the "Add farm" button — only field agents can add farms now */}
          <div className="flex items-center gap-2 text-xs text-ink-500 dark:text-ink-400">
            <Icon.Info className="w-4 h-4" />
            <span>To add a new farm, contact your field agent.</span>
          </div>
        </div>
      </Card>

      <Card padding="lg" className="farmer-section-card">
        <SectionHeader eyebrow="Regulatory evidence" title="My EUDR plot registry" subtitle="Read-only status of the evidence-backed geometry registered for your farms." icon={<Icon.Shield className="w-4 h-4" />} />
        {plotsLoading ? <div className="py-4"><Spinner size="sm" /></div> : regulatoryPlots.length === 0 ? (
          <div className="mt-4"><Alert tone="info">No evidence-backed EUDR plot has been registered yet. Your field agent must complete the mapping evidence workflow.</Alert></div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {regulatoryPlots.map((plot:any) => (
              <div key={plot.id} className="rounded-xl border border-ink-200 dark:border-ink-800 p-4">
                <div className="flex items-start justify-between gap-3"><div><p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{plot.farm_name} · {plot.plot_code}</p><p className="text-xs text-ink-500 mt-1">Geometry version {plot.geometry_version || '—'} · {plot.geometry_status || 'Not approved'}</p></div><StatusPill tone={plot.status === 'APPROVED' ? 'success' : plot.status === 'FIELD_VERIFICATION_REQUIRED' || plot.status === 'REJECTED' ? 'danger' : 'warning'}>{plot.status}</StatusPill></div>
                <div className="grid grid-cols-2 gap-3 mt-3 text-xs"><div><p className="text-ink-500">Approved area</p><p className="font-semibold nums">{plot.area_ha != null ? `${Number(plot.area_ha).toFixed(2)} ha` : '—'}</p></div><div><p className="text-ink-500">Approved</p><p className="font-semibold">{plot.approved_at ? new Date(plot.approved_at).toLocaleDateString() : '—'}</p></div></div>
                <p className="text-2xs text-ink-500 mt-3">Only approved geometry is shown as the regulatory farm boundary. Raw field traces and internal risk signals remain restricted to authorized reviewers.</p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ─── Farm cards (each with a polygon map) ─── */}
      {farms.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<Icon.Map className="w-5 h-5" />}
            title="No farms mapped yet"
            description="Your field agent will map your farm boundary during onboarding. If you believe this is an error, please contact your cooperative or field agent."
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {farms.map(farm => {
            const badge = EUDR_BADGE[farm.eudr_status] || EUDR_BADGE.UNMAPPED;
            return (
              <Card key={farm.id} padding="none" className="farmer-farm-card overflow-hidden">
                <div className="p-5 border-b border-ink-200 dark:border-ink-800">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{farm.name}</p>
                        {farm.is_primary && (
                          <span className="text-2xs px-1.5 py-0.5 rounded bg-admin-50 dark:bg-admin-500/10 text-admin-700 dark:text-admin-500 font-medium">
                            Primary
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-ink-500 dark:text-ink-400 flex items-center gap-1.5">
                        <Icon.MapPin className="w-3 h-3" />
                        {farm.village || '—'}{farm.cooperative ? ` · ${farm.cooperative}` : ''}
                      </p>
                    </div>
                    <StatusPill tone={badge.tone} dot>{badge.label}</StatusPill>
                  </div>
                </div>

                {/* Polygon map */}
                <div className="border-b border-ink-200 dark:border-ink-800">
                  {farm.gps_polygon ? (
                    <FarmPolygonViewer
                      polygon={farm.gps_polygon}
                      centroid={farm.centroid_lat && farm.centroid_lng ? { lat: farm.centroid_lat, lng: farm.centroid_lng } : null}
                      areaHa={farm.polygon_area_hectares}
                      height="280px"
                      label={farm.name}
                      eudrStatus={farm.eudr_status}
                      eudrRiskLevel={farm.eudr_risk_level || undefined}  // v3.11.16: null → undefined
                    />
                  ) : (
                    <div className="h-[200px] flex items-center justify-center bg-ink-100 dark:bg-ink-900 text-center px-6">
                      <div>
                        <Icon.MapPin className="w-7 h-7 mx-auto text-ink-400 dark:text-ink-600 mb-2" />
                        <p className="text-sm text-ink-500 dark:text-ink-400">No boundary mapped yet.</p>
                        {/* v1.7.6: farmer cannot map boundary — only field agents can */}
                        <p className="text-2xs text-ink-400 mt-2 max-w-[200px]">
                          Contact your field agent to map this farm's boundary.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Stats + actions */}
                <div className="p-5 space-y-3">
                  <div className="farmer-farm-stats grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <p className="text-ink-500 dark:text-ink-400">Area</p>
                      <p className="text-sm font-semibold nums text-ink-900 dark:text-ink-50">{Number(farm.polygon_area_hectares || 0).toFixed(2)} ha</p>
                    </div>
                    <div>
                      <p className="text-ink-500 dark:text-ink-400">Primary crop</p>
                      <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 capitalize">{farm.primary_crop || '—'}</p>
                    </div>
                    <div>
                      <p className="text-ink-500 dark:text-ink-400">EUDR risk</p>
                      <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{farm.eudr_risk_level || '—'}</p>
                    </div>
                    <div>
                      <p className="text-ink-500 dark:text-ink-400">Verified</p>
                      <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">
                        {farm.eudr_verified_at ? new Date(farm.eudr_verified_at).toLocaleDateString() : '—'}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-ink-200 dark:border-ink-800">
                    {/* v1.7.6: farmer cannot edit/delete farms — only field agents can manage farms.
                        The farmer can only view their farms and set a primary farm (preference only). */}
                    {!farm.is_primary && (
                      <button
                        onClick={() => handleSetPrimary(farm.id)}
                        className="btn-secondary text-xs"
                      >
                        Set as primary
                      </button>
                    )}
                    <div className="ml-auto flex items-center gap-1.5 text-2xs text-ink-400">
                      <Icon.Info className="w-3.5 h-3.5" />
                      <span>To edit or delete this farm, contact your field agent.</span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

    </div>
  );
};

/* ─── Carbon Credit Section ─── */
const CarbonCreditSection: React.FC<{ farmerId: string }> = ({ farmerId }) => {
  const [balance, setBalance] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState('');
  const [redeemStatus, setRedeemStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const txPag = usePagination(transactions, 5);

  const fetchData = useCallback(async () => {
    try {
      const [balRes, txRes] = await Promise.all([
        apiClient.get(`/eudr/balance/${farmerId}`).catch(() => ({ data: { balance: null } })),
        apiClient.get(`/eudr/transactions/${farmerId}`).catch(() => ({ data: { transactions: [] } })),
      ]);
      setBalance(balRes.data.balance);
      setTransactions(txRes.data.transactions || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [farmerId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRedeem = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setRedeemStatus(null);
    try {
      const res = await apiClient.post('/eudr/redeem', {
        points: parseFloat(redeemPoints),
        farmer_id: farmerId,
      });
      setRedeemStatus(`✅ ${redeemPoints} points redeemed for ${res.data.monetary_value.toLocaleString()} XAF!`);
      setTimeout(() => { setShowRedeemModal(false); setRedeemStatus(null); setRedeemPoints(''); fetchData(); }, 3000);
    } catch (err: any) {
      setRedeemStatus(err.response?.data?.error || 'Failed to redeem points.');
    } finally { setSubmitting(false); }
  };

  if (loading) return null;
  if (!balance || parseFloat(balance.points_available) <= 0) return null;

  return (
    <Card padding="none" className="farmer-carbon-card mb-6 overflow-hidden">
      <div className="p-5 border-b border-ink-200 dark:border-ink-800 flex items-center justify-between gap-3">
        <SectionHeader
          eyebrow="Sustainability"
          title="Carbon credits"
          subtitle="Earned through sustainable cocoa practices."
          icon={<Icon.Leaf className="w-4 h-4" />}
        />
        <button
          onClick={() => setShowRedeemModal(true)}
          disabled={parseFloat(balance.points_available) < parseFloat(balance.conversion_rate || 100)}
          className="btn-accent text-xs brand-surface-agrarian shrink-0"
        >
          <Icon.Wallet className="w-3.5 h-3.5" /> Redeem
        </button>
      </div>

      <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Available" value={Number(balance.points_available).toFixed(1)} hint="points" accent="agrarian" />
        <MetricTile label="Total earned" value={Number(balance.total_points).toFixed(1)} hint="points" />
        <MetricTile label="Redeemed" value={Number(balance.points_redeemed).toFixed(1)} hint="points" />
        <MetricTile label="Current value" value={Number(balance.current_value_xaf).toLocaleString()} hint="XAF" />
      </div>

      {txPag.total > 0 && (
        <div className="overflow-x-auto border-t border-ink-200 dark:border-ink-800">
          <table className="data-table mobile-record-table farmer-carbon-records">
            <thead>
              <tr><th>Type</th><th>Points</th><th>Value (XAF)</th><th>Date</th></tr>
            </thead>
            <tbody>
              {txPag.paginatedItems.map((tx: any) => (
                <tr key={tx.id}>
                  <td>
                    {tx.type === 'ACCRUAL'
                      ? <StatusPill tone="success" dot>Accrual</StatusPill>
                      : <StatusPill tone="info" dot>Redemption</StatusPill>}
                  </td>
                  <td className="nums font-medium">{Number(tx.points).toFixed(1)}</td>
                  <td className="nums text-ink-900 dark:text-ink-50">{Number(tx.monetary_value_xaf).toLocaleString()}</td>
                  <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{new Date(tx.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="border-t border-ink-200 dark:border-ink-800">
            <Pagination
              page={txPag.page}
              pageSize={txPag.pageSize}
              total={txPag.total}
              onPageChange={txPag.setPage}
            />
          </div>
        </div>
      )}

      {showRedeemModal && (
        <>
          <div className="sheet-backdrop" onClick={() => setShowRedeemModal(false)} />
          <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6">
            <Card padding="lg" className="max-w-md w-full sheet md:rounded-2xl md:shadow-e6 animate-sheet-up md:animate-scale-in">
              <div className="sheet-handle md:hidden" />
              <div className="flex items-center justify-between mb-4">
                <SectionHeader title="Redeem carbon points" icon={<Icon.Award className="w-4 h-4" />} />
                <IconButton label="Close" onClick={() => setShowRedeemModal(false)} size="sm">
                  <Icon.X className="w-4 h-4" />
                </IconButton>
              </div>

              <div className="mb-4 p-3.5 rounded-lg surface-inset">
                <p className="text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">Available points</p>
                <p className="text-2xl font-semibold nums text-ink-900 dark:text-ink-50">{Number(balance.points_available).toFixed(1)}</p>
                <p className="text-2xs text-ink-500 dark:text-ink-400 mt-1">Rate: 1 point = {balance.conversion_rate} XAF</p>
              </div>

              <form onSubmit={handleRedeem}>
                <div className="mb-4">
                  <label htmlFor="r-points" className="label">Points to redeem</label>
                  <input
                    id="r-points"
                    type="number"
                    step="0.1"
                    required
                    max={balance.points_available}
                    value={redeemPoints}
                    onChange={(e) => setRedeemPoints(e.target.value)}
                    className="input"
                  />
                  {redeemPoints && parseFloat(redeemPoints) > 0 && (
                    <p className="mt-1.5 text-xs text-success-700 dark:text-success-500 nums">
                      You'll receive {(parseFloat(redeemPoints) * balance.conversion_rate).toLocaleString()} XAF
                    </p>
                  )}
                </div>
                {redeemStatus && (
                  <div className="mb-3">
                    <Alert tone={redeemStatus.includes('✅') ? 'success' : 'danger'}>
                      {redeemStatus.replace(/[✅❌]/g, '').trim()}
                    </Alert>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-accent w-full py-3 brand-surface-agrarian"
                >
                  {submitting ? (
                    <><Spinner size="sm" /> <span>Processing…</span></>
                  ) : (
                    <><Icon.Send className="w-4 h-4" /> <span>Redeem now</span></>
                  )}
                </button>
              </form>
            </Card>
          </div>
        </>
      )}
    </Card>
  );
};

/* ─── EUDR Status Section ─── */
const EudrStatusSection: React.FC<{ farmerId: string }> = ({ farmerId }) => {
  const [compliant, setCompliant] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get(`/eudr/plot-verifications`).catch(() => ({ data: { verifications: [] } }))
      .then((res: any) => {
        const verifications = res.data?.verifications || [];
        const farmerVerification = verifications.find((v: any) => v.farmer_id === farmerId && v.status === 'VERIFIED');
        setCompliant(!!farmerVerification);
        setLoading(false);
      });
  }, [farmerId]);

  if (loading) return null;

  return (
    <Card padding="md" className="farmer-eudr-card mb-6">
      <div className="farmer-eudr-layout flex items-start gap-3">
        <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
          compliant
            ? 'bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-500'
            : 'bg-warning-50 dark:bg-warning-500/10 text-warning-700 dark:text-warning-500'
        }`}>
          <Icon.Shield className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="farmer-eudr-head flex items-center justify-between gap-2 mb-1">
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">EUDR compliance</h3>
            {compliant
              ? <StatusPill tone="success" dot>Compliant</StatusPill>
              : <StatusPill tone="warning" dot>Verification pending</StatusPill>}
          </div>
          <p className="text-xs text-ink-500 dark:text-ink-400 text-pretty">
            {compliant
              ? 'Your farm boundary has been verified. Your batches are eligible for EU export.'
              : 'Your farm boundary GPS polygon needs verification by the admin team.'}
          </p>
        </div>
      </div>
    </Card>
  );
};

/* ─── Summary row (clickable, used in overview tab) ─── */
const SummaryRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  hint?: string;
  onClick?: () => void;
}> = ({ icon, label, value, hint, onClick }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={`farmer-summary-row w-full flex items-center gap-3 p-3.5 rounded-lg surface-inset text-left transition-all ${
      onClick ? 'hover:bg-ink-100 dark:hover:bg-ink-800 cursor-pointer' : 'cursor-default'
    }`}
  >
    <span className="farmer-summary-icon shrink-0 w-9 h-9 rounded-lg bg-ink-0 dark:bg-ink-900 flex items-center justify-center text-ink-500 dark:text-ink-400 border border-ink-200 dark:border-ink-800">
      {icon}
    </span>
    <div className="farmer-summary-copy min-w-0 flex-1">
      <p className="farmer-summary-label text-2xs uppercase tracking-micro text-ink-500 dark:text-ink-400">{label}</p>
      <p className="farmer-summary-value text-lg font-semibold nums text-ink-900 dark:text-ink-50">{value}</p>
      {hint && <p className="text-2xs text-ink-400 dark:text-ink-500">{hint}</p>}
    </div>
    {onClick && (
      <svg viewBox="0 0 24 24" className="w-4 h-4 text-ink-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18l6-6-6-6" />
      </svg>
    )}
  </button>
);

/* ─── Pest Scanner Tab (AI-powered disease/pest identification) ─── */
const PestScannerTab: React.FC<{ farmerId: string }> = ({ farmerId }) => {
  const [photo, setPhoto] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<PestScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<any[]>([]);

  const fetchReports = useCallback(async () => {
    try {
      const r = await aiApi.getMyPestReports();
      setReports(r.reports);
    } catch {}
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Image too large (max 10MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setPhoto(reader.result as string);
      setResult(null);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!photo) return;
    setScanning(true);
    setError(null);
    try {
      const r = await aiApi.scanPest(photo);
      setResult(r);
      fetchReports();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Scan failed. Please try again.');
    } finally { setScanning(false); }
  };

  const severityTone: Record<string, 'success' | 'warning' | 'danger'> = {
    low: 'success', moderate: 'warning', high: 'danger', critical: 'danger',
  };

  return (
    <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-pest-scanner space-y-6">
      <Card padding="lg">
        <SectionHeader
          eyebrow="AI scanner"
          title="Pest & disease scanner"
          subtitle="Take a photo of your cocoa plant and the AI will identify any pests or diseases."
          icon={<Icon.Leaf className="w-4 h-4" />}
        />
        <div className="mt-4 space-y-4">
          {/* Photo upload */}
          <div>
            <label className="label">Upload plant photo</label>
            <input type="file" accept="image/*" capture="environment" onChange={handleFile} className="input" />
          </div>

          {/* Preview */}
          {photo && (
            <div className="relative rounded-xl overflow-hidden border border-ink-200 dark:border-ink-800">
              <img src={photo} alt="Plant preview" className="w-full max-h-80 object-cover" />
              <button onClick={() => { setPhoto(null); setResult(null); }}
                className="absolute top-2 right-2 btn-icon-sm bg-ink-0/90 dark:bg-ink-850/90">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
          )}

          {error && <Alert tone="danger">{error}</Alert>}

          {photo && !result && (
            <button onClick={handleScan} disabled={scanning}
              className="btn-accent w-full py-3 brand-surface-agrarian">
              {scanning ? <><Spinner size="sm" /> <span>AI scanning photo…</span></> : <><Icon.Leaf className="w-4 h-4" /> <span>Scan with AI</span></>}
            </button>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-3">
              <div className={`p-4 rounded-lg border ${
                result.severity === 'critical' || result.severity === 'high' ? 'bg-danger-50 dark:bg-danger-500/10 border-danger-500/20'
                : result.severity === 'moderate' ? 'bg-warning-50 dark:bg-warning-500/10 border-warning-500/20'
                : 'bg-success-50 dark:bg-success-500/10 border-success-500/20'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-bold text-ink-900 dark:text-ink-50">{result.diagnosis}</p>
                  <StatusPill tone={severityTone[result.severity] || 'warning'} dot>{result.severity}</StatusPill>
                </div>
                <p className="text-2xs text-ink-500 dark:text-ink-400 nums">Confidence: {(result.confidence * 100).toFixed(0)}%</p>
              </div>
              {result.description && (
                <div className="p-3.5 surface-inset rounded-lg">
                  <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Description</p>
                  <p className="text-sm text-ink-700 dark:text-ink-200 text-pretty">{result.description}</p>
                </div>
              )}
              {result.recommendations && (
                <div className="p-3.5 surface-inset rounded-lg">
                  <p className="text-2xs uppercase tracking-micro text-ink-400 dark:text-ink-500 mb-1">Recommendations</p>
                  <p className="text-sm text-ink-700 dark:text-ink-200 text-pretty">{result.recommendations}</p>
                </div>
              )}
              <p className="text-2xs text-success-700 dark:text-success-500">
                Report saved and sent to admin for review.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Previous reports */}
      {reports.length > 0 && (
        <Card padding="none" className="overflow-hidden">
          <div className="p-5 border-b border-ink-200 dark:border-ink-800">
            <SectionHeader eyebrow="History" title="My scan reports" icon={<Icon.Clock className="w-4 h-4" />} />
          </div>
          <div className="overflow-x-auto">
            <table className="data-table mobile-record-table farmer-pest-records">
              <thead><tr><th>Diagnosis</th><th>Severity</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id}>
                    <td className="text-sm font-medium text-ink-900 dark:text-ink-50">{r.diagnosis}</td>
                    <td><StatusPill tone={severityTone[r.severity] || 'warning'} dot>{r.severity}</StatusPill></td>
                    <td><SmartStatus status={r.status} /></td>
                    <td className="text-2xs text-ink-500 dark:text-ink-400 nums">{new Date(r.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default FarmerDashboard;

/* ═══════════════════════════════════════════════════════════════
 * v1.9.4 — Farmer Sustainability Summary
 * Shows farmer's sustainability tier, points, and activity stats
 * ═══════════════════════════════════════════════════════════════ */
const FarmerSustainabilitySummary: React.FC<{ farmerId: string }> = ({ farmerId }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/post-harvest/sustainability/farmer/${farmerId}`);
      setData(res.data?.sustainability || null);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [farmerId]);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-4"><Spinner /></div></Card>;
  if (!data || data.total_points === 0) {
    return (
      <Card padding="lg" className="farmer-sustainability-summary farmer-section-card">
        <SectionHeader eyebrow="Sustainability Program" title="Your Sustainability Score" subtitle="Earn points by recording good post-harvest practices" icon={<Icon.TrendingUp className="w-4 h-4" />} />
        <div className="mt-4 p-4 rounded-xl bg-info-50 dark:bg-info-500/10 text-center">
          <span className="farmer-empty-icon"><Icon.Leaf className="w-7 h-7" /></span>
          <p className="text-sm font-medium">No sustainability points yet</p>
          <p className="text-xs text-ink-500 mt-1">Go to the "Farm Activities" tab and record your harvest, fermentation, and drying to earn points!</p>
        </div>
      </Card>
    );
  }

  const tierColors: Record<string, string> = {
    BRONZE: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-500/20 dark:text-amber-400',
    SILVER: 'bg-gray-200 text-gray-700 border-gray-400 dark:bg-gray-500/20 dark:text-gray-300',
    GOLD: 'bg-yellow-100 text-yellow-800 border-yellow-400 dark:bg-yellow-500/20 dark:text-yellow-400',
    PLATINUM: 'bg-purple-100 text-purple-800 border-purple-400 dark:bg-purple-500/20 dark:text-purple-400',
  };
  const tierEmoji: Record<string, string> = { BRONZE: '', SILVER: '', GOLD: '', PLATINUM: '' };
  const tier = data.tier || 'BRONZE';

  return (
    <Card padding="lg" className="farmer-sustainability-summary farmer-section-card">
      <SectionHeader eyebrow="Sustainability Program" title="Your Sustainability Score" subtitle="Earned from good post-harvest practices" icon={<Icon.TrendingUp className="w-4 h-4" />} />
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* Tier badge */}
        <div className={`p-4 rounded-xl border-2 text-center ${tierColors[tier] || tierColors.BRONZE}`}>
          <span className="farmer-tier-icon"><Icon.Award className="w-5 h-5" /></span>
          <p className="text-lg font-bold">{tier}</p>
          <p className="text-2xs opacity-80">Tier</p>
        </div>
        {/* Total points */}
        <div className="p-4 rounded-xl bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-800 text-center">
          <span className="farmer-tier-icon"><Icon.TrendingUp className="w-5 h-5" /></span>
          <p className="text-lg font-bold nums">{data.total_points || 0}</p>
          <p className="text-2xs text-ink-500">Total Points</p>
        </div>
        {/* Activities */}
        <div className="p-4 rounded-xl bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-800 text-center">
          <span className="farmer-tier-icon"><Icon.Activity className="w-5 h-5" /></span>
          <p className="text-lg font-bold nums">{data.activities_count || 0}</p>
          <p className="text-2xs text-ink-500">Activities Recorded</p>
        </div>
        {/* Perfect practices */}
        <div className="p-4 rounded-xl bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-800 text-center">
          <span className="text-3xl block mb-1">✨</span>
          <p className="text-lg font-bold nums">{(data.perfect_fermentations || 0) + (data.perfect_dryings || 0)}</p>
          <p className="text-2xs text-ink-500">Perfect Practices</p>
        </div>
      </div>
      {/* Progress to next tier */}
      {tier !== 'PLATINUM' && (
        <div className="mt-4">
          {(() => {
            const nextTier = tier === 'BRONZE' ? 'SILVER' : tier === 'SILVER' ? 'GOLD' : 'PLATINUM';
            const nextThreshold = tier === 'BRONZE' ? 50 : tier === 'SILVER' ? 100 : 200;
            const progress = Math.min(((data.total_points || 0) / nextThreshold) * 100, 100);
            return (
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-ink-500">Progress to {nextTier}</span>
                  <span className="nums font-medium">{data.total_points || 0} / {nextThreshold} pts</span>
                </div>
                <div className="h-2 bg-ink-200 dark:bg-ink-700 rounded-full overflow-hidden">
                  <div className="h-full bg-success-500 transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
            );
          })()}
        </div>
      )}
      {tier === 'PLATINUM' && (
        <Alert tone="success">You've reached PLATINUM tier — the highest sustainability level. Keep up the excellent practices.</Alert>
      )}
      {/* Zero-waste collections */}
      {(data.zero_waste_collections || 0) > 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs text-success-600 dark:text-success-400">
          <span>♻️</span>
          <span>{data.zero_waste_collections} waste items collected for zero-waste processing</span>
        </div>
      )}
    </Card>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.3 — Farmer Activities Tab (ILLITERATE-FRIENDLY DESIGN)
 * Big pictogram buttons, minimal text, visual selectors, plain language
 * Designed for farmers who may not read — icons + colors + simple words
 * ═══════════════════════════════════════════════════════════════ */
const FarmerActivitiesTab: React.FC<{ farmerId: string; farms: any[] }> = ({ farmerId, farms }) => {
  const [activities, setActivities] = useState<any[]>([]);
  const [dryingCenters, setDryingCenters] = useState<any[]>([]);
  const [fermCenters, setFermCenters] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [step, setStep] = useState(1); // 1=pick activity, 2=details
  const [form, setForm] = useState({
    activity_type: '', farm_id: '', activity_date: new Date().toISOString().slice(0, 10),
    activity_end_date: '', // v1.9.1: date range for fermentation/drying periods
    quantity_kg: '', pod_count: '',
    fermentation_method: '', fermentation_days: '', fermentation_turns: '', fermentation_center_id: '',
    drying_method: '', drying_days: '', moisture_pct: '', drying_center_id: '',
    bean_color: '', notes: ''
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const [aRes, dRes, fRes] = await Promise.all([
        apiClient.get(`/post-harvest/activities/farmer/${farmerId}`).catch(() => ({ data: { activities: [] } })),
        apiClient.get('/post-harvest/drying-centers').catch(() => ({ data: { centers: [] } })),
        apiClient.get('/post-harvest/fermentation-centers').catch(() => ({ data: { centers: [] } })),
      ]);
      setActivities(aRes.data?.activities || []);
      setDryingCenters(dRes.data?.centers || []);
      setFermCenters(fRes.data?.centers || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [farmerId]);

  const handleCreate = async () => {
    setError(null);
    try {
      const payload: any = {
        farmer_id: farmerId,
        farm_id: form.farm_id || undefined,
        activity_type: form.activity_type,
        activity_date: form.activity_date,
        activity_end_date: form.activity_end_date || undefined, // v1.9.1: date range
        quantity_kg: form.quantity_kg ? parseFloat(form.quantity_kg) : undefined,
        pod_count: form.pod_count ? parseInt(form.pod_count) : undefined,
        notes: form.notes || undefined,
        bean_color: form.bean_color || undefined,
      };
      if (form.activity_type === 'FERMENTATION') {
        payload.fermentation_method = form.fermentation_method || undefined;
        payload.fermentation_days = form.fermentation_days ? parseInt(form.fermentation_days) : undefined;
        payload.fermentation_turns = form.fermentation_turns ? parseInt(form.fermentation_turns) : undefined;
        payload.fermentation_center_id = form.fermentation_center_id || undefined;
      }
      if (form.activity_type === 'DRYING') {
        payload.drying_method = form.drying_method || undefined;
        payload.drying_days = form.drying_days ? parseInt(form.drying_days) : undefined;
        payload.moisture_pct = form.moisture_pct ? parseFloat(form.moisture_pct) : undefined;
        payload.drying_center_id = form.drying_center_id || undefined;
      }
      const res = await apiClient.post('/post-harvest/activities', payload);
      setSuccess('Saved. Your activity has been recorded.');
      setShowCreate(false);
      setStep(1);
      setForm({ activity_type: '', farm_id: '', activity_date: new Date().toISOString().slice(0, 10), activity_end_date: '', quantity_kg: '', pod_count: '', fermentation_method: '', fermentation_days: '', fermentation_turns: '', fermentation_center_id: '', drying_method: '', drying_days: '', moisture_pct: '', drying_center_id: '', bean_color: '', notes: '' });
      refresh();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) { setError(err.response?.data?.error || 'Something went wrong. Please try again.'); }
  };

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  // v1.8.3: Activity type definitions with PICTOGRAMS for illiterate users
  const ACTIVITY_TYPES = [
    { value: 'HARVEST', icon: <Icon.Package className="w-5 h-5" />, label: 'Harvest', simpleLabel: 'Pick Pods', color: 'bg-success-100 dark:bg-success-500/20 border-success-300', textColor: 'text-success-700 dark:text-success-400' },
    { value: 'FERMENTATION', icon: <Icon.Layers className="w-5 h-5" />, label: 'Ferment', simpleLabel: 'Box/Basket', color: 'bg-info-100 dark:bg-info-500/20 border-info-300', textColor: 'text-info-700 dark:text-info-400' },
    { value: 'DRYING', icon: <Icon.TrendingUp className="w-5 h-5" />, label: 'Dry', simpleLabel: 'Dry Beans', color: 'bg-warning-100 dark:bg-warning-500/20 border-warning-300', textColor: 'text-warning-700 dark:text-warning-400' },
    { value: 'BAGGING', icon: <Icon.Package className="w-5 h-5" />, label: 'Bag', simpleLabel: 'Put in Bags', color: 'bg-ink-100 dark:bg-ink-800 border-ink-300', textColor: 'text-ink-700 dark:text-ink-300' },
  ];

  // v1.8.3: Visual bean color selector with colored circles
  const BEAN_COLORS = [
    { value: 'BROWN', label: 'Brown (Good)', color: 'bg-amber-700', emoji: '🟤' },
    { value: 'DARK_BROWN', label: 'Dark Brown', color: 'bg-amber-900', emoji: '⚫' },
    { value: 'PURPLE', label: 'Purple (Not Ready)', color: 'bg-purple-600', emoji: '🟣' },
    { value: 'MIXED', label: 'Mixed Colors', color: 'bg-gradient-to-br from-amber-700 to-purple-600', emoji: '🔵' },
    { value: 'SLATE', label: 'Slate (Raw)', color: 'bg-gray-500', emoji: '⚪' },
  ];

  // v1.8.3: Visual drying method selector
  const DRYING_METHODS = [
    { value: 'SUN', icon: <Icon.TrendingUp className="w-4 h-4" />, label: 'Sun' },
    { value: 'SOLAR_DRYER', icon: <Icon.TrendingUp className="w-4 h-4" />, label: 'Solar' },
    { value: 'WOOD_FIRE', icon: <Icon.Activity className="w-4 h-4" />, label: 'Fire' },
    { value: 'MECHANICAL', icon: <Icon.Layers className="w-4 h-4" />, label: 'Machine' },
  ];

  // v1.8.3: Visual fermentation method selector
  const FERM_METHODS = [
    { value: 'WOODEN_BOX', icon: <Icon.Package className="w-4 h-4" />, label: 'Box' },
    { value: 'BASKET', icon: <Icon.Layers className="w-4 h-4" />, label: 'Basket' },
    { value: 'HEAP', icon: <Icon.Layers className="w-4 h-4" />, label: 'Heap' },
    { value: 'BARREL', icon: <Icon.Package className="w-4 h-4" />, label: 'Barrel' },
  ];

  return (
    <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-activities space-y-4">
      {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert tone="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Simple intro in PLAIN LANGUAGE */}
      <Card padding="md" className="farmer-activities-intro-card bg-info-50 dark:bg-info-500/10 border-info-200 dark:border-info-500/30">
        <div className="flex items-start gap-3">
          <span className="farmer-activities-intro-icon"><Icon.Activity className="w-5 h-5" /></span>
          <div>
            <p className="text-sm font-medium text-ink-900 dark:text-ink-50">Tell us what you did on your farm</p>
            <p className="text-xs text-ink-600 dark:text-ink-400 mt-0.5">Tap a button below to record your work. It's easy — just pick what you did and answer a few questions.</p>
          </div>
        </div>
      </Card>

      {/* BIG PICTOGRAM BUTTONS — Step 1: Pick what you did */}
      {activities.length > 0 && (
        <Card padding="none" className="overflow-hidden farmer-activities-records-card">
          <div className="p-4 border-b border-ink-200 dark:border-ink-800 flex justify-between items-center farmer-activities-records-head">
            <p className="text-sm font-semibold">Your Records</p>
            <button onClick={() => { setShowCreate(true); setStep(1); }} className="btn-primary text-sm py-2.5 px-4">
              Add New
            </button>
          </div>
          {/* ACTIVITY CARDS — visual, not table (illiterate-friendly) */}
          <div className="divide-y divide-ink-100 dark:divide-ink-800">
            {activities.slice(0, 20).map(a => {
              const actType = ACTIVITY_TYPES.find(t => t.value === a.activity_type);
              return (
                <div key={a.id} className="p-4 flex items-center gap-3 hover:bg-ink-50 dark:hover:bg-ink-800/50">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${actType?.color || 'bg-ink-100'}`}>
                    {actType?.icon || <Icon.Activity className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink-900 dark:text-ink-50">{actType?.simpleLabel || a.activity_type}</p>
                    <p className="text-xs text-ink-500">
                      {/* v1.9.1: show date range as period for fermentation/drying */}
                      {a.activity_end_date
                        ? `${new Date(a.activity_date).toLocaleDateString()} → ${new Date(a.activity_end_date).toLocaleDateString()}`
                        : new Date(a.activity_date).toLocaleDateString()}
                      {a.quantity_kg ? ` · ${Number(a.quantity_kg).toFixed(0)} kg` : ''}
                      {a.moisture_pct ? ` · ${Number(a.moisture_pct).toFixed(1)}% wet` : ''}
                      {/* v1.9.1: show sustainability points if any */}
                      {a.sustainability_points > 0 ? ` · ${a.sustainability_points} pts` : ''}
                    </p>
                  </div>
                  {a.drying_center_name && <span className="text-2xs text-ink-400 hidden md:inline">{a.drying_center_name}</span>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* EMPTY STATE with big button */}
      {activities.length === 0 && (
        <Card padding="lg" className="text-center">
          <div className="py-8">
            <span className="farmer-empty-icon"><Icon.Leaf className="w-7 h-7" /></span>
            <p className="text-lg font-semibold text-ink-900 dark:text-ink-50 mb-2">No records yet</p>
            <p className="text-sm text-ink-500 mb-6">Start by telling us about your harvest, fermentation, or drying.</p>
            <button onClick={() => { setShowCreate(true); setStep(1); }} className="btn-primary text-base py-3 px-8">
              Start Recording
            </button>
          </div>
        </Card>
      )}

      {/* SIMPLIFIED CREATE SHEET — Step-based, visual, minimal text */}
      {showCreate && (
        <Sheet open={showCreate} onClose={() => { setShowCreate(false); setStep(1); }} title={step === 1 ? 'What did you do?' : 'Tell us more'}
          footer={
            <div className="flex gap-2">
              {step === 2 && <button onClick={() => setStep(1)} className="btn-secondary flex-1 text-sm py-2.5">← Back</button>}
              {step === 1
                ? <button onClick={() => form.activity_type && setStep(2)} disabled={!form.activity_type} className="btn-primary flex-1 text-sm py-2.5">Next →</button>
                : <button onClick={handleCreate} className="btn-primary flex-1 text-sm py-2.5">Save</button>
              }
            </div>
          }>

          {step === 1 && (
            /* STEP 1: BIG PICTOGRAM BUTTONS — "What did you do?" */
            <div className="space-y-3">
              <p className="text-sm font-medium text-center text-ink-700 dark:text-ink-300 mb-4">Choose the activity you completed.</p>
              <div className="grid grid-cols-2 gap-3">
                {ACTIVITY_TYPES.map(t => (
                  <button
                    key={t.value}
                    onClick={() => setForm({ ...form, activity_type: t.value })}
                    className={`p-5 rounded-2xl border-2 transition-all text-center ${form.activity_type === t.value ? `${t.color} border-current ${t.textColor} scale-105 shadow-lg` : 'border-ink-200 dark:border-ink-800 hover:border-ink-300'}`}
                  >
                    <span className="farmer-activity-type-icon">{t.icon}</span>
                    <span className="text-base font-bold block">{t.simpleLabel}</span>
                    <span className="text-xs text-ink-500 block mt-0.5">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            /* STEP 2: DETAILS — minimal fields, visual selectors, plain language */
            <div className="space-y-4">
              {/* v1.9.1: DATE RANGE for fermentation/drying, single date for harvest/bagging */}
              {(form.activity_type === 'FERMENTATION' || form.activity_type === 'DRYING') ? (
                <div>
                  <label className="label text-sm">From when to when?</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <input type="date" className="input text-base py-2.5" value={form.activity_date} onChange={e => setForm({ ...form, activity_date: e.target.value })} />
                      <p className="text-xs text-ink-400 mt-1 text-center">Start</p>
                    </div>
                    <div>
                      <input type="date" className="input text-base py-2.5" value={form.activity_end_date} onChange={e => setForm({ ...form, activity_end_date: e.target.value })} />
                      <p className="text-xs text-ink-400 mt-1 text-center">End</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="label text-sm">When did you do this?</label>
                  <input type="date" className="input text-base py-2.5" value={form.activity_date} onChange={e => setForm({ ...form, activity_date: e.target.value })} />
                </div>
              )}

              {/* Farm — simple dropdown */}
              {farms.length > 0 && (
                <div>
                  <label className="label text-sm">Which farm?</label>
                  <select className="input text-base py-2.5" value={form.farm_id} onChange={e => setForm({ ...form, farm_id: e.target.value })}>
                    <option value="">My farm</option>
                    {farms.map((f: any) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              )}

              {/* Quantity — with helpful label */}
              <div>
                <label className="label text-sm">How much? (kg)</label>
                <input type="number" step="0.1" className="input text-base py-2.5" value={form.quantity_kg} onChange={e => setForm({ ...form, quantity_kg: e.target.value })} placeholder="e.g. 50" />
                <p className="text-xs text-ink-400 mt-1">Don't know exact? Just estimate.</p>
              </div>

              {/* FERMENTATION — visual method selector (simplified: no days count, date range captures duration) */}
              {form.activity_type === 'FERMENTATION' && (
                <div className="p-4 rounded-xl bg-info-50 dark:bg-info-500/10 space-y-3">
                  <p className="text-sm font-medium">How did you ferment?</p>
                  <div className="grid grid-cols-4 gap-2">
                    {FERM_METHODS.map(m => (
                      <button key={m.value} onClick={() => setForm({ ...form, fermentation_method: m.value })}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${form.fermentation_method === m.value ? 'border-info-500 bg-info-100 dark:bg-info-500/20 scale-105' : 'border-ink-200 dark:border-ink-700'}`}>
                        <span className="farmer-method-icon">{m.icon}</span>
                        <span className="text-xs font-medium">{m.label}</span>
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs text-ink-500">How many times did you turn the beans?</label>
                    <input type="number" className="input text-base py-2" value={form.fermentation_turns} onChange={e => setForm({ ...form, fermentation_turns: e.target.value })} placeholder="e.g. 2" />
                  </div>
                  <div>
                    <label className="text-xs text-ink-500">Where? (optional)</label>
                    <select className="input text-base py-2" value={form.fermentation_center_id} onChange={e => setForm({ ...form, fermentation_center_id: e.target.value })}>
                      <option value="">At my farm</option>
                      {fermCenters.map(c => <option key={c.id} value={c.id}>{c.center_name}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* DRYING — visual method selector (simplified: no days count, date range captures duration) */}
              {form.activity_type === 'DRYING' && (
                <div className="p-4 rounded-xl bg-warning-50 dark:bg-warning-500/10 space-y-3">
                  <p className="text-sm font-medium">How did you dry?</p>
                  <div className="grid grid-cols-4 gap-2">
                    {DRYING_METHODS.map(m => (
                      <button key={m.value} onClick={() => setForm({ ...form, drying_method: m.value })}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${form.drying_method === m.value ? 'border-warning-500 bg-warning-100 dark:bg-warning-500/20 scale-105' : 'border-ink-200 dark:border-ink-700'}`}>
                        <span className="farmer-method-icon">{m.icon}</span>
                        <span className="text-xs font-medium">{m.label}</span>
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-xs text-ink-500">Moisture % (if known)</label>
                    <input type="number" step="0.1" className="input text-base py-2" value={form.moisture_pct} onChange={e => setForm({ ...form, moisture_pct: e.target.value })} placeholder="7.0" />
                    <p className="text-xs text-ink-400 mt-0.5">Don't know? Leave it blank.</p>
                  </div>
                  <div>
                    <label className="text-xs text-ink-500">Where? (optional)</label>
                    <select className="input text-base py-2" value={form.drying_center_id} onChange={e => setForm({ ...form, drying_center_id: e.target.value })}>
                      <option value="">At my farm</option>
                      {dryingCenters.map(c => <option key={c.id} value={c.id}>{c.center_name}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* BEAN COLOR — visual colored circle selector */}
              {(form.activity_type === 'FERMENTATION' || form.activity_type === 'DRYING') && (
                <div>
                  <label className="label text-sm">Bean color</label>
                  <div className="flex gap-2 flex-wrap">
                    {BEAN_COLORS.map(c => (
                      <button key={c.value} onClick={() => setForm({ ...form, bean_color: c.value })}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all ${form.bean_color === c.value ? 'border-ink-900 dark:border-ink-50 scale-105' : 'border-ink-200 dark:border-ink-700'}`}>
                        <span className={`w-5 h-5 rounded-full ${c.color}`} />
                        <span className="text-xs font-medium">{c.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes — optional, plain language */}
              <div>
                <label className="label text-sm">Anything else? (optional)</label>
                <textarea className="input text-base py-2.5" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Beans look good, no problems" />
              </div>
            </div>
          )}
        </Sheet>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.1 — Farmer Input Credit Tab
 * Farmer sees their input distributions + balance + repayment history
 * ═══════════════════════════════════════════════════════════════ */
const FarmerInputCreditTab: React.FC<{ farmerId: string }> = ({ farmerId }) => {
  const [distributions, setDistributions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/farm-inputs/farmer/${farmerId}`);
      setDistributions(res.data?.distributions || []);
    } catch { setDistributions([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [farmerId]);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  const totalBalance = distributions.reduce((sum: number, d: any) => sum + Number(d.balance_xaf || 0), 0);
  const totalCredit = distributions.reduce((sum: number, d: any) => sum + Number(d.total_value_xaf || 0), 0);
  const totalRepaid = distributions.reduce((sum: number, d: any) => sum + Number(d.amount_repaid_xaf || 0), 0);

  return (
    <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-input-credit space-y-4">
      {/* Summary */}
      <div className="farmer-input-credit-summary grid grid-cols-3 gap-3">
        <Card padding="md">
          <p className="text-2xs uppercase tracking-micro text-ink-500">Total Credit Received</p>
          <p className="text-xl font-bold nums text-ink-900 dark:text-ink-50 mt-1">{totalCredit.toLocaleString()} XAF</p>
        </Card>
        <Card padding="md">
          <p className="text-2xs uppercase tracking-micro text-ink-500">Total Repaid</p>
          <p className="text-xl font-bold nums text-success-600 mt-1">{totalRepaid.toLocaleString()} XAF</p>
        </Card>
        <Card padding="md" className={totalBalance > 0 ? 'border-2 border-warning-300' : ''}>
          <p className="text-2xs uppercase tracking-micro text-ink-500">Outstanding Balance</p>
          <p className={`text-xl font-bold nums mt-1 ${totalBalance > 0 ? 'text-warning-600' : 'text-success-600'}`}>{totalBalance.toLocaleString()} XAF</p>
        </Card>
      </div>

      {totalBalance > 0 && (
        <Alert tone="warning">
          <strong>You have {totalBalance.toLocaleString()} XAF in outstanding input credit.</strong> This will be
          automatically deducted from your next batch payment. The deduction happens when the finance team
          approves your batch payout — you don't need to do anything.
        </Alert>
      )}

      {/* Distributions table */}
      <Card padding="none" className="overflow-hidden farmer-input-credit-ledger">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800 farmer-record-section-head">
          <SectionHeader eyebrow="Credit Ledger" title="My Input Credit History" subtitle="Fertilizers, fungicides, seedlings distributed to you on credit" />
        </div>
        {distributions.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Package className="w-5 h-5" />} title="No input distributions" description="You have not received any inputs on credit. Contact your field agent if you need inputs." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table mobile-record-table farmer-input-credit-records">
              <thead><tr><th>Date</th><th>Input</th><th>Category</th><th>Quantity</th><th>Total Value</th><th>Repaid</th><th>Balance</th><th>Status</th></tr></thead>
              <tbody>
                {distributions.map(d => (
                  <tr key={d.id} className={d.status === 'OUTSTANDING' ? 'bg-warning-50/30 dark:bg-warning-500/5' : ''}>
                    <td className="text-2xs">{new Date(d.distribution_date).toLocaleDateString()}</td>
                    <td className="text-sm font-medium">{d.input_name || '---'}</td>
                    <td><StatusPill tone="info" dot>{d.input_category || '---'}</StatusPill></td>
                    <td className="nums">{d.quantity} {d.unit}</td>
                    <td className="nums">{Number(d.total_value_xaf).toLocaleString()}</td>
                    <td className="nums text-success-600">{Number(d.amount_repaid_xaf || 0).toLocaleString()}</td>
                    <td className={`nums font-medium ${Number(d.balance_xaf) > 0 ? 'text-warning-600' : 'text-success-600'}`}>{Number(d.balance_xaf).toLocaleString()}</td>
                    <td><StatusPill tone={d.status === 'OUTSTANDING' ? 'warning' : d.status === 'PARTIALLY_REPAID' ? 'info' : 'success'} dot>{d.status.replace(/_/g, ' ')}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.1 — Farmer Quality Tab
 * Farmer sees lab tests + organoleptic results for their batches
 * ═══════════════════════════════════════════════════════════════ */
const FarmerQualityTab: React.FC<{ farmerId: string }> = ({ farmerId }) => {
  const [batches, setBatches] = useState<any[]>([]);
  const [labTests, setLabTests] = useState<any[]>([]);
  const [organoTests, setOrganoTests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      // Get farmer's batches
      const batchRes = await apiClient.get('/farmer/me');
      const farmerBatches = batchRes.data?.stats?.recent_batches || batchRes.data?.recent_batches || [];
      setBatches(farmerBatches);

      // For each intaked batch, fetch lab tests + organoleptic
      const labResults: any[] = [];
      const organoResults: any[] = [];
      for (const batch of farmerBatches.slice(0, 20)) {
        try {
          const labRes = await apiClient.get(`/lab-tests/${batch.id}`).catch(() => null);
          if (labRes?.data?.lab_tests) labResults.push(...labRes.data.lab_tests);
        } catch {}
        try {
          const organoRes = await apiClient.get(`/organoleptic/${batch.id}`).catch(() => null);
          if (organoRes?.data?.tests) organoResults.push(...organoRes.data.tests);
        } catch {}
      }
      setLabTests(labResults);
      setOrganoTests(organoResults);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [farmerId]);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-quality space-y-4">
      {/* Lab Tests */}
      <Card padding="none" className="overflow-hidden farmer-quality-record-card">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="EU Compliance" title="Lab Test Results (MRL / Cadmium)" subtitle="Heavy metal + pesticide residue tests for your batches" icon={<Icon.Shield className="w-4 h-4" />} />
        </div>
        {labTests.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Shield className="w-5 h-5" />} title="No lab tests yet" description="Lab test results will appear here when your batches are tested." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table mobile-record-table farmer-quality-lab-records">
              <thead><tr><th>Test Ref</th><th>Lab</th><th>Date</th><th>Cadmium</th><th>Result</th></tr></thead>
              <tbody>
                {labTests.map(t => (
                  <tr key={t.id}>
                    <td className="text-xs font-mono">{t.test_reference}</td>
                    <td className="text-sm">{t.lab_name}</td>
                    <td className="text-2xs">{new Date(t.test_date).toLocaleDateString()}</td>
                    <td className={`nums ${t.cadmium_pass === false ? 'text-danger-600 font-bold' : 'text-success-600'}`}>{t.cadmium_mg_kg ? `${Number(t.cadmium_mg_kg).toFixed(3)} mg/kg` : '---'}</td>
                    <td><StatusPill tone={t.overall_result === 'PASS' ? 'success' : t.overall_result === 'FAIL' ? 'danger' : 'warning'} dot>{t.overall_result}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Organoleptic */}
      <Card padding="none" className="overflow-hidden farmer-quality-record-card">
        <div className="p-5 border-b border-ink-200 dark:border-ink-800">
          <SectionHeader eyebrow="Sensory Analysis" title="Organoleptic Test Results" subtitle="Aroma, flavor, smoke contamination assessments" icon={<Icon.Leaf className="w-4 h-4" />} />
        </div>
        {organoTests.length === 0 ? (
          <div className="p-8"><EmptyState icon={<Icon.Leaf className="w-5 h-5" />} title="No sensory tests yet" description="Sensory analysis results will appear here when your batches are tested." /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table mobile-record-table farmer-quality-sensory-records">
              <thead><tr><th>Date</th><th>Score</th><th>Smoke Odor</th><th>Smoke Taste</th><th>Disposition</th></tr></thead>
              <tbody>
                {organoTests.map(t => (
                  <tr key={t.id} className={t.disposition === 'REJECTED' ? 'bg-danger-50/50 dark:bg-danger-500/5' : ''}>
                    <td className="text-2xs">{new Date(t.tested_at).toLocaleDateString()}</td>
                    <td className={`nums font-medium ${Number(t.overall_score) >= 8 ? 'text-success-600' : Number(t.overall_score) >= 6 ? 'text-warning-600' : 'text-danger-600'}`}>{t.overall_score ? Number(t.overall_score).toFixed(1) : '---'}</td>
                    <td>{t.smoke_odor_detected ? <StatusPill tone="warning" dot>{t.smoke_odor_intensity}</StatusPill> : <span className="text-success-600 text-xs">None</span>}</td>
                    <td>{t.smoke_taste_detected ? <StatusPill tone="warning" dot>{t.smoke_taste_intensity}</StatusPill> : <span className="text-success-600 text-xs">None</span>}</td>
                    <td><StatusPill tone={t.disposition === 'APPROVED_PREMIUM' ? 'success' : t.disposition === 'REJECTED' ? 'danger' : 'warning'} dot>{t.disposition.replace(/_/g, ' ')}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 * v1.8.1 — Farmer Yield Forecast Tab
 * Farmer sees their farm's biological capacity + utilization
 * ═══════════════════════════════════════════════════════════════ */
const FarmerYieldTab: React.FC<{ farmerId: string; farms: any[] }> = ({ farmerId, farms }) => {
  const [forecasts, setForecasts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      // Fetch yield forecasts for each farm
      const results: any[] = [];
      for (const farm of farms) {
        try {
          const res = await apiClient.get(`/yield/forecasts/${farm.id}`).catch(() => null);
          if (res?.data?.forecast) results.push({ ...res.data.forecast, farm_name: farm.name });
        } catch {}
      }
      setForecasts(results);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, [farmerId, farms.length]);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-6"><Spinner /></div></Card>;

  return (
    <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-yield space-y-4">
      <Alert tone="info">
        <strong>Yield Forecast</strong> predicts the maximum biological capacity of your farm based on tree count,
        cocoa variety, tree age, and weather conditions. This helps prevent fraud and ensures fair pricing.
        If your actual deliveries exceed the forecast, the system will flag it for review.
      </Alert>

      {forecasts.length === 0 ? (
        <Card padding="lg">
          <EmptyState icon={<Icon.TrendingUp className="w-5 h-5" />} title="No yield forecasts yet" description="Forecasts are generated automatically when your farm has tree data and batches are logged." />
        </Card>
      ) : (
        forecasts.map(f => (
          <Card key={f.id} padding="lg" className="farmer-yield-forecast-card">
            <SectionHeader eyebrow="Biological capacity" title={f.farm_name || 'Farm'} subtitle={`Season ${f.season_year}`} icon={<Icon.TrendingUp className="w-4 h-4" />} />
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <p className="text-2xs uppercase tracking-micro text-ink-500">Trees</p>
                <p className="text-lg font-bold nums">{f.tree_count || '---'}</p>
              </div>
              <div>
                <p className="text-2xs uppercase tracking-micro text-ink-500">Max Capacity</p>
                <p className="text-lg font-bold nums text-ink-900 dark:text-ink-50">{Number(f.max_expected_kg || 0).toFixed(0)} kg</p>
              </div>
              <div>
                <p className="text-2xs uppercase tracking-micro text-ink-500">Delivered</p>
                <p className={`text-lg font-bold nums ${Number(f.utilization_pct) > 100 ? 'text-warning-600' : 'text-success-600'}`}>{Number(f.cumulative_delivered_kg || 0).toFixed(0)} kg</p>
              </div>
              <div>
                <p className="text-2xs uppercase tracking-micro text-ink-500">Utilization</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-ink-200 dark:bg-ink-700 rounded-full overflow-hidden">
                    <div className={`h-full ${Number(f.utilization_pct) > 150 ? 'bg-danger-500' : Number(f.utilization_pct) > 100 ? 'bg-warning-500' : 'bg-success-500'}`} style={{ width: `${Math.min(Number(f.utilization_pct || 0), 100)}%` }} />
                  </div>
                  <span className={`text-sm font-bold nums ${Number(f.utilization_pct) > 150 ? 'text-danger-600' : Number(f.utilization_pct) > 100 ? 'text-warning-600' : 'text-success-600'}`}>{Number(f.utilization_pct || 0).toFixed(0)}%</span>
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-ink-500">Variety:</span> {f.cocoa_variety || 'Traditional'}</div>
              <div><span className="text-ink-500">Age Factor:</span> {Number(f.age_factor * 100).toFixed(0)}% productive</div>
              <div><span className="text-ink-500">Remaining:</span> <span className="nums font-medium">{Number(f.remaining_capacity_kg || 0).toFixed(0)} kg</span></div>
              <div><span className="text-ink-500">Status:</span> <StatusPill tone={f.fraud_alert_level === 'CLEAR' ? 'success' : f.fraud_alert_level === 'WARNING' ? 'warning' : 'danger'} dot>{f.fraud_alert_level}</StatusPill></div>
            </div>
          </Card>
        ))
      )}
    </div>
  );
};

/* ─── v2.5: Farmer AI Assistant Tab (5 AI agents: Disease, Yield, Fertilizer, Weather, Knowledge) ─── */
const FarmerAIAssistantTab: React.FC<{ farmerId: string; farms: any[]; villageName: string }> = ({ farmerId, farms, villageName }) => { // v2.5
  // v2.5: Disease Diagnosis state
  const [diseaseInput, setDiseaseInput] = useState<{ symptoms: string; photoUrl: string; farmId: string }>({ symptoms: '', photoUrl: '', farmId: farms[0]?.id || '' }); // v2.5
  const [diseaseResult, setDiseaseResult] = useState<any>(null); // v2.5
  const [diseaseLoading, setDiseaseLoading] = useState(false); // v2.5
  const [diseaseError, setDiseaseError] = useState<string | null>(null); // v2.5

  // v2.5: Yield Prediction state
  const [yieldInput, setYieldInput] = useState<{ farmId: string; treeAgeYears: string; areaHectares: string }>({ farmId: farms[0]?.id || '', treeAgeYears: '5', areaHectares: '1' }); // v2.5
  const [yieldResult, setYieldResult] = useState<any>(null); // v2.5
  const [yieldLoading, setYieldLoading] = useState(false); // v2.5
  const [yieldError, setYieldError] = useState<string | null>(null); // v2.5

  // v2.5: Fertilizer Recommendation state
  const [fertInput, setFertInput] = useState<{ farmId: string; soilPh: string; nitrogen: string; phosphorus: string; potassium: string }>({ farmId: farms[0]?.id || '', soilPh: '6.0', nitrogen: '20', phosphorus: '15', potassium: '10' }); // v2.5
  const [fertResult, setFertResult] = useState<any>(null); // v2.5
  const [fertLoading, setFertLoading] = useState(false); // v2.5
  const [fertError, setFertError] = useState<string | null>(null); // v2.5

  // v2.5: Weather Advisory state
  const [weatherInput, setWeatherInput] = useState<{ farmId: string }>({ farmId: farms[0]?.id || '' }); // v2.5
  const [weatherResult, setWeatherResult] = useState<any>(null); // v2.5
  const [weatherLoading, setWeatherLoading] = useState(false); // v2.5
  const [weatherError, setWeatherError] = useState<string | null>(null); // v2.5

  // v2.5: Knowledge Search state
  const [knowledgeQuery, setKnowledgeQuery] = useState(''); // v2.5
  const [knowledgeResult, setKnowledgeResult] = useState<any>(null); // v2.5
  const [knowledgeLoading, setKnowledgeLoading] = useState(false); // v2.5
  const [knowledgeError, setKnowledgeError] = useState<string | null>(null); // v2.5

  // v2.5: Disease Diagnosis handler → POST /api/ai-agents/DISEASE/run with {symptoms, photoUrl, farmId, villageName}
  const handleDiagnose = async () => { // v2.5
    setDiseaseLoading(true); setDiseaseError(null); setDiseaseResult(null); // v2.5
    try {
      const res = await apiClient.post('/ai-agents/DISEASE/run', { symptoms: diseaseInput.symptoms, photoUrl: diseaseInput.photoUrl, farmId: diseaseInput.farmId, villageName }); // v2.5
      setDiseaseResult(res.data); // v2.5
    } catch (err: any) { setDiseaseError(err.response?.data?.error || err.message || 'Diagnosis failed'); } // v2.5
    finally { setDiseaseLoading(false); } // v2.5
  };

  // v2.5: Yield Prediction handler → POST /api/ai-agents/YIELD/run with {farmId, treeAgeYears, areaHectares}
  const handlePredictYield = async () => { // v2.5
    setYieldLoading(true); setYieldError(null); setYieldResult(null); // v2.5
    try {
      const res = await apiClient.post('/ai-agents/YIELD/run', { farmId: yieldInput.farmId, treeAgeYears: parseFloat(yieldInput.treeAgeYears) || undefined, areaHectares: parseFloat(yieldInput.areaHectares) || undefined }); // v2.5
      setYieldResult(res.data); // v2.5
    } catch (err: any) { setYieldError(err.response?.data?.error || err.message || 'Yield prediction failed'); } // v2.5
    finally { setYieldLoading(false); } // v2.5
  };

  // v2.5: Fertilizer Recommendation handler → POST /api/ai-agents/FERTILIZER/run
  const handleRecommendFertilizer = async () => { // v2.5
    setFertLoading(true); setFertError(null); setFertResult(null); // v2.5
    try {
      const res = await apiClient.post('/ai-agents/FERTILIZER/run', { farmId: fertInput.farmId || undefined, soilPh: parseFloat(fertInput.soilPh) || undefined, soilNitrogen: parseFloat(fertInput.nitrogen) || undefined, soilPhosphorus: parseFloat(fertInput.phosphorus) || undefined, soilPotassium: parseFloat(fertInput.potassium) || undefined }); // v3.2.12: fixed field names to match agent input shape
      setFertResult(res.data); // v2.5
    } catch (err: any) { setFertError(err.response?.data?.error || err.message || 'Fertilizer recommendation failed'); } // v2.5
    finally { setFertLoading(false); } // v2.5
  };

  // v2.5: Weather Advisory handler → POST /api/ai-agents/WEATHER_INTEL/run with {farmId, villageName}
  const handleGetWeather = async () => { // v2.5
    setWeatherLoading(true); setWeatherError(null); setWeatherResult(null); // v2.5
    try {
      const res = await apiClient.post('/ai-agents/WEATHER_INTEL/run', { farmId: weatherInput.farmId, villageName }); // v2.5
      setWeatherResult(res.data); // v2.5
    } catch (err: any) { setWeatherError(err.response?.data?.error || err.message || 'Weather advisory failed'); } // v2.5
    finally { setWeatherLoading(false); } // v2.5
  };

  // v2.5: Knowledge Search handler → GET /api/knowledge/search/hybrid?q=...
  const handleSearchKnowledge = async () => { // v2.5
    if (!knowledgeQuery.trim()) return; // v2.5
    setKnowledgeLoading(true); setKnowledgeError(null); setKnowledgeResult(null); // v2.5
    try {
      const res = await apiClient.get(`/knowledge/search/hybrid`, { params: { q: knowledgeQuery, limit: 10 } }); // v2.5
      setKnowledgeResult(res.data); // v2.5
    } catch (err: any) { setKnowledgeError(err.response?.data?.error || err.message || 'Knowledge search failed'); } // v2.5
    finally { setKnowledgeLoading(false); } // v2.5
  };

  // v2.5: helper — render confidence score as a colored pill (0-100 scale)
  const renderConfidence = (confidence: any) => { // v2.5
    if (confidence === undefined || confidence === null) return null; // v2.5
    const pct = Math.round(Number(confidence)); // v2.5
    if (isNaN(pct)) return null; // v2.5
    const tone: 'success' | 'warning' | 'danger' = pct >= 75 ? 'success' : pct >= 50 ? 'warning' : 'danger'; // v2.5
    return <StatusPill tone={tone} dot>Confidence {pct}%</StatusPill>; // v2.5
  };

  return ( // v2.5
    <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-ai-assistant space-y-6"> {/* v2.5 */}
      {/* v2.5: tab intro banner */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="v2.5 AI Agents" // v2.5
          title="AI Assistant" // v2.5
          subtitle="Run CoVECA's production AI agents — disease diagnosis, yield prediction, fertilizer recommendation, weather advisory, and knowledge search. Powered by the admin-configured Model Router (Gemini / DeepSeek / Qwen / Florence-2 / GPT-4o / Claude)." // v2.5
          icon={<Icon.Activity className="w-4 h-4" />} // v2.5
        />
      </Card>

      {/* v2.5: ═══ Disease Diagnosis ═══ */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="Agent 1 · DISEASE" // v2.5
          title="Disease Diagnosis" // v2.5
          subtitle="Describe symptoms and (optionally) paste a photo URL. AI cross-references IRAD/CABI knowledge base." // v2.5
          icon={<Icon.Shield className="w-4 h-4" />} // v2.5
        />
        <div className="mt-4 space-y-3"> {/* v2.5 */}
          <div> {/* v2.5 */}
            <label className="label">Farm (optional — used for context)</label> {/* v2.5 */}
            <select className="input" value={diseaseInput.farmId} onChange={e => setDiseaseInput({ ...diseaseInput, farmId: e.target.value })}> {/* v2.5 */}
              <option value="">No specific farm</option> {/* v2.5 */}
              {farms.map((f: any) => <option key={f.id} value={f.id}>{f.name || f.id.slice(0, 8)}</option>)} {/* v2.5 */}
            </select>
          </div>
          <div> {/* v2.5 */}
            <label className="label">Symptoms *</label> {/* v2.5 */}
            <textarea className="input" rows={3} value={diseaseInput.symptoms} onChange={e => setDiseaseInput({ ...diseaseInput, symptoms: e.target.value })} placeholder="e.g. Brown spots on pods, yellowing leaves, wilting branches…" /> {/* v2.5 */}
          </div>
          <div> {/* v2.5 */}
            <label className="label">Photo URL (optional)</label> {/* v2.5 */}
            <input type="url" className="input" value={diseaseInput.photoUrl} onChange={e => setDiseaseInput({ ...diseaseInput, photoUrl: e.target.value })} placeholder="https://example.com/photo.jpg" /> {/* v2.5 */}
          </div>
          <button onClick={handleDiagnose} disabled={diseaseLoading || !diseaseInput.symptoms.trim()} className="btn-primary text-sm"> {/* v2.5 */}
            {diseaseLoading ? <><Spinner size="sm" /> Diagnosing…</> : <><Icon.Shield className="w-4 h-4" /> Diagnose</>} {/* v2.5 */}
          </button>
          {diseaseError && <Alert tone="danger" onClose={() => setDiseaseError(null)}>{diseaseError}</Alert>} {/* v2.5 */}
          {diseaseResult && ( // v2.5
            <div className="p-4 surface-inset rounded-lg space-y-2"> {/* v2.5 */}
              <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50"> {/* v2.5 */}
                  {diseaseResult.output?.diagnosis || diseaseResult.output?.raw_text?.slice(0, 80) || 'Diagnosis result'} {/* v2.5 */}
                </h4>
                {renderConfidence(diseaseResult.output?.confidence)} {/* v2.5 */}
              </div>
              {diseaseResult.output?.severity && <p className="text-xs text-ink-500">Severity: <span className="font-medium capitalize">{String(diseaseResult.output.severity)}</span></p>} {/* v2.5 */}
              {diseaseResult.output?.treatment_plan && Array.isArray(diseaseResult.output.treatment_plan) && ( // v2.5
                <div> {/* v2.5 */}
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Treatment plan</p> {/* v2.5 */}
                  <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                    {diseaseResult.output.treatment_plan.map((step: string, i: number) => <li key={i}>{step}</li>)} {/* v2.5 */}
                  </ul>
                </div>
              )}
              {diseaseResult.output?.preventive_advice && Array.isArray(diseaseResult.output.preventive_advice) && ( // v2.5
                <div> {/* v2.5 */}
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Preventive advice</p> {/* v2.5 */}
                  <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                    {diseaseResult.output.preventive_advice.map((step: string, i: number) => <li key={i}>{step}</li>)} {/* v2.5 */}
                  </ul>
                </div>
              )}
              {diseaseResult.output?.isolate_trees !== undefined && ( // v2.5
                <p className="text-xs"> {/* v2.5 */}
                  <span className="text-ink-500">Isolate trees:</span>{' '} {/* v2.5 */}
                  <span className={`font-medium ${diseaseResult.output.isolate_trees ? 'text-warning-600' : 'text-success-600'}`}> {/* v2.5 */}
                    {diseaseResult.output.isolate_trees ? 'Yes - recommended' : 'No'} {/* v2.5 */}
                  </span>
                </p>
              )}
              {diseaseResult.output?.reasoning && <p className="text-xs text-ink-500 dark:text-ink-400 italic border-t border-ink-200 dark:border-ink-800 pt-2">{diseaseResult.output.reasoning}</p>} {/* v2.5 */}
              {!diseaseResult.success && diseaseResult.error && <Alert tone="danger">{diseaseResult.error}</Alert>} {/* v2.5 */}
            </div>
          )}
        </div>
      </Card>

      {/* v2.5: ═══ Yield Prediction ═══ */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="Agent 2 · YIELD" // v2.5
          title="Yield Prediction" // v2.5
          subtitle="Forecast next season's cocoa yield based on tree age, farm area, and historical batch data." // v2.5
          icon={<Icon.TrendingUp className="w-4 h-4" />} // v2.5
        />
        <div className="mt-4 space-y-3"> {/* v2.5 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3"> {/* v2.5 */}
            <div> {/* v2.5 */}
              <label className="label">Farm</label> {/* v2.5 */}
              <select className="input" value={yieldInput.farmId} onChange={e => setYieldInput({ ...yieldInput, farmId: e.target.value })}> {/* v2.5 */}
                <option value="">No specific farm</option> {/* v2.5 */}
                {farms.map((f: any) => <option key={f.id} value={f.id}>{f.name || f.id.slice(0, 8)}</option>)} {/* v2.5 */}
              </select>
            </div>
            <div> {/* v2.5 */}
              <label className="label">Tree age (years)</label> {/* v2.5 */}
              <input type="number" min="0" step="0.5" className="input" value={yieldInput.treeAgeYears} onChange={e => setYieldInput({ ...yieldInput, treeAgeYears: e.target.value })} /> {/* v2.5 */}
            </div>
            <div> {/* v2.5 */}
              <label className="label">Area (hectares)</label> {/* v2.5 */}
              <input type="number" min="0" step="0.1" className="input" value={yieldInput.areaHectares} onChange={e => setYieldInput({ ...yieldInput, areaHectares: e.target.value })} /> {/* v2.5 */}
            </div>
          </div>
          <button onClick={handlePredictYield} disabled={yieldLoading} className="btn-primary text-sm"> {/* v2.5 */}
            {yieldLoading ? <><Spinner size="sm" /> Predicting…</> : <><Icon.TrendingUp className="w-4 h-4" /> Predict Yield</>} {/* v2.5 */}
          </button>
          {yieldError && <Alert tone="danger" onClose={() => setYieldError(null)}>{yieldError}</Alert>} {/* v2.5 */}
          {yieldResult && ( // v2.5
            <div className="p-4 surface-inset rounded-lg space-y-2"> {/* v2.5 */}
              <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Forecast result</h4> {/* v2.5 */}
                {renderConfidence(yieldResult.output?.confidence_pct)} {/* v2.5 */}
              </div>
              {yieldResult.output?.yield_kg_forecast !== undefined && ( // v2.5
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2"> {/* v2.5 */}
                  <div><p className="text-2xs uppercase tracking-micro text-ink-500">Forecast</p><p className="text-lg font-bold nums">{Number(yieldResult.output.yield_kg_forecast).toLocaleString()} kg</p></div> {/* v2.5 */}
                  {yieldResult.output?.confidence_interval && ( // v2.5
                    <div> {/* v2.5 */}
                      <p className="text-2xs uppercase tracking-micro text-ink-500">Confidence interval</p> {/* v2.5 */}
                      <p className="text-sm font-medium nums"> {/* v2.5 */}
                        {Number(yieldResult.output.confidence_interval.low).toLocaleString()} - {Number(yieldResult.output.confidence_interval.high).toLocaleString()} kg {/* v2.5 */}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {yieldResult.output?.risk_factors && Array.isArray(yieldResult.output.risk_factors) && ( // v2.5
                <div> {/* v2.5 */}
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Risk factors</p> {/* v2.5 */}
                  <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                    {yieldResult.output.risk_factors.map((risk: string, i: number) => <li key={i}>{risk}</li>)} {/* v2.5 */}
                  </ul>
                </div>
              )}
              {yieldResult.output?.optimization_advice && Array.isArray(yieldResult.output.optimization_advice) && ( // v2.5
                <div> {/* v2.5 */}
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Optimization advice</p> {/* v2.5 */}
                  <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v2.5 */}
                    {yieldResult.output.optimization_advice.map((tip: string, i: number) => <li key={i}>{tip}</li>)} {/* v2.5 */}
                  </ul>
                </div>
              )}
              {yieldResult.output?.reasoning && <p className="text-xs text-ink-500 dark:text-ink-400 italic border-t border-ink-200 dark:border-ink-800 pt-2">{yieldResult.output.reasoning}</p>} {/* v2.5 */}
              {!yieldResult.success && yieldResult.error && <Alert tone="danger">{yieldResult.error}</Alert>} {/* v2.5 */}
            </div>
          )}
        </div>
      </Card>

      {/* v2.5: ═══ Fertilizer Recommendation ═══ */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="Agent 3 · FERTILIZER" // v2.5
          title="Fertilizer Recommendation" // v2.5
          subtitle="Provide soil test results (pH, N, P, K) for site-specific fertilizer guidance." // v2.5
          icon={<Icon.Leaf className="w-4 h-4" />} // v2.5
        />
        <div className="mt-4 space-y-3"> {/* v2.5 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3"> {/* v2.5 */}
            <div> {/* v2.5 */}
              <label className="label">Soil pH</label> {/* v2.5 */}
              <input type="number" step="0.1" className="input" value={fertInput.soilPh} onChange={e => setFertInput({ ...fertInput, soilPh: e.target.value })} /> {/* v2.5 */}
            </div>
            <div> {/* v2.5 */}
              <label className="label">Nitrogen (mg/kg)</label> {/* v2.5 */}
              <input type="number" step="0.1" className="input" value={fertInput.nitrogen} onChange={e => setFertInput({ ...fertInput, nitrogen: e.target.value })} /> {/* v2.5 */}
            </div>
            <div> {/* v2.5 */}
              <label className="label">Phosphorus (mg/kg)</label> {/* v2.5 */}
              <input type="number" step="0.1" className="input" value={fertInput.phosphorus} onChange={e => setFertInput({ ...fertInput, phosphorus: e.target.value })} /> {/* v2.5 */}
            </div>
            <div> {/* v2.5 */}
              <label className="label">Potassium (mg/kg)</label> {/* v2.5 */}
              <input type="number" step="0.1" className="input" value={fertInput.potassium} onChange={e => setFertInput({ ...fertInput, potassium: e.target.value })} /> {/* v2.5 */}
            </div>
          </div>
          <button onClick={handleRecommendFertilizer} disabled={fertLoading} className="btn-primary text-sm"> {/* v2.5 */}
            {fertLoading ? <><Spinner size="sm" /> Recommending…</> : <><Icon.Leaf className="w-4 h-4" /> Recommend</>} {/* v2.5 */}
          </button>
          {fertError && <Alert tone="danger" onClose={() => setFertError(null)}>{fertError}</Alert>} {/* v2.5 */}
          {fertResult && ( // v2.5
            <div className="p-4 surface-inset rounded-lg space-y-2"> {/* v2.5 */}
              <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Recommendation</h4> {/* v2.5 */}
                {renderConfidence(fertResult.output?.confidence)} {/* v2.5 */}
              </div>
              {fertResult.output?.npk_formulation && <p className="text-sm"><span className="text-ink-500">Recommended formula:</span> <span className="font-medium">{fertResult.output.npk_formulation}</span></p>} {/* v3.2.13 */}
              {fertResult.output?.quantity_kg_per_ha && <p className="text-sm"><span className="text-ink-500">Application rate:</span> <span className="font-medium">{fertResult.output.quantity_kg_per_ha} kg/ha</span></p>} {/* v3.2.13 */}
              {fertResult.output?.application_schedule && Array.isArray(fertResult.output.application_schedule) && ( // v3.2.13
                <div> {/* v3.2.13 */}
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-500 mb-1">Schedule</p> {/* v3.2.13 */}
                  <ul className="text-sm text-ink-700 dark:text-ink-300 list-disc pl-5 space-y-0.5"> {/* v3.2.13 */}
                    {fertResult.output.application_schedule.map((s: string, i: number) => <li key={i}>{s}</li>)} {/* v3.2.13 */}
                  </ul>
                </div>
              )}
              {fertResult.output?.organic_matter_advice && <p className="text-sm"><span className="text-ink-500">Organic matter:</span> <span className="font-medium">{fertResult.output.organic_matter_advice}</span></p>} {/* v3.2.13 */}
              {fertResult.output?.cost_estimate_xaf && <p className="text-sm"><span className="text-ink-500">Est. cost:</span> <span className="font-medium">{fertResult.output.cost_estimate_xaf} XAF</span></p>} {/* v3.2.13 */}
              {fertResult.output?.reasoning && <p className="text-xs text-ink-500 dark:text-ink-400 italic border-t border-ink-200 dark:border-ink-800 pt-2">{fertResult.output.reasoning}</p>} {/* v2.5 */}
              {fertResult.output?.raw_text && !fertResult.output.npk_formulation && <p className="text-sm text-ink-700 dark:text-ink-300 whitespace-pre-wrap">{fertResult.output.raw_text}</p>} {/* v3.2.13 */}
              {!fertResult.success && fertResult.error && <Alert tone="danger">{fertResult.error}</Alert>} {/* v2.5 */}
            </div>
          )}
        </div>
      </Card>

      {/* v2.5: ═══ Weather Advisory ═══ */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="Agent 4 · WEATHER_INTEL" // v2.5
          title="Weather Advisory" // v2.5
          subtitle="Get a farming-specific weather advisory for your village and farm." // v2.5
          icon={<Icon.MapPin className="w-4 h-4" />} // v2.5
        />
        <div className="mt-4 space-y-3"> {/* v2.5 */}
          <div> {/* v2.5 */}
            <label className="label">Farm (used for context)</label> {/* v2.5 */}
            <select className="input" value={weatherInput.farmId} onChange={e => setWeatherInput({ farmId: e.target.value })}> {/* v2.5 */}
              <option value="">No specific farm</option> {/* v2.5 */}
              {farms.map((f: any) => <option key={f.id} value={f.id}>{f.name || f.id.slice(0, 8)}</option>)} {/* v2.5 */}
            </select>
            {villageName && <p className="text-2xs text-ink-500 mt-1">Village: <span className="font-medium">{villageName}</span></p>} {/* v2.5 */}
          </div>
          <button onClick={handleGetWeather} disabled={weatherLoading} className="btn-primary text-sm"> {/* v2.5 */}
            {weatherLoading ? <><Spinner size="sm" /> Fetching…</> : <><Icon.MapPin className="w-4 h-4" /> Get Weather</>} {/* v2.5 */}
          </button>
          {weatherError && <Alert tone="danger" onClose={() => setWeatherError(null)}>{weatherError}</Alert>} {/* v2.5 */}
          {weatherResult && ( // v2.5
            <div className="p-4 surface-inset rounded-lg space-y-2"> {/* v2.5 */}
              <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Weather advisory</h4> {/* v2.5 */}
                {renderConfidence(weatherResult.output?.confidence)} {/* v2.5 */}
              </div>
              {weatherResult.output?.farm_advisory && <p className="text-sm text-ink-700 dark:text-ink-300">{weatherResult.output.farm_advisory}</p>} {/* v3.2.13 */}
              {weatherResult.output?.current_conditions && <p className="text-sm"><span className="text-ink-500">Current:</span> <span className="font-medium">{weatherResult.output.current_conditions}</span></p>} {/* v3.2.13 */}
              {weatherResult.output?.forecast_7day && <p className="text-sm"><span className="text-ink-500">7-day forecast:</span> <span className="font-medium">{weatherResult.output.forecast_7day}</span></p>} {/* v3.2.13 */}
              {weatherResult.output?.rainfall_prediction && <p className="text-sm"><span className="text-ink-500">Rainfall:</span> <span className="font-medium">{weatherResult.output.rainfall_prediction}</span></p>} {/* v3.2.13 */}
              {weatherResult.output?.extreme_alerts && Array.isArray(weatherResult.output.extreme_alerts) && weatherResult.output.extreme_alerts.length > 0 && ( // v3.2.13
                <div className="p-2 rounded-md bg-red-50 dark:bg-red-950/30 border-l-4 border-red-500"> {/* v3.2.13 */}
                  <p className="text-2xs font-medium uppercase tracking-micro text-red-600 mb-1">⚠️ Extreme Alerts</p> {/* v3.2.13 */}
                  <ul className="text-sm text-red-700 dark:text-red-400 list-disc pl-5 space-y-0.5"> {/* v3.2.13 */}
                    {weatherResult.output.extreme_alerts.map((alert: string, i: number) => <li key={i}>{alert}</li>)} {/* v3.2.13 */}
                  </ul>
                </div>
              )}
              {weatherResult.output?.reasoning && <p className="text-xs text-ink-500 dark:text-ink-400 italic border-t border-ink-200 dark:border-ink-800 pt-2">{weatherResult.output.reasoning}</p>} {/* v2.5 */}
              {weatherResult.output?.raw_text && !weatherResult.output.farm_advisory && <p className="text-sm text-ink-700 dark:text-ink-300 whitespace-pre-wrap">{weatherResult.output.raw_text}</p>} {/* v3.2.13 */}
              {!weatherResult.success && weatherResult.error && <Alert tone="danger">{weatherResult.error}</Alert>} {/* v2.5 */}
            </div>
          )}
        </div>
      </Card>

      {/* v2.5: ═══ Knowledge Search ═══ */}
      <Card padding="lg"> {/* v2.5 */}
        <SectionHeader // v2.5
          eyebrow="Hybrid vector + keyword search" // v2.5
          title="Knowledge Search" // v2.5
          subtitle="Search the Cameroon Cocoa Digital Twin knowledge base (IRAD, CABI, MINADER documents)." // v2.5
          icon={<Icon.BookOpen className="w-4 h-4" />} // v2.5
        />
        <div className="mt-4 space-y-3"> {/* v2.5 */}
          <div className="flex gap-2"> {/* v2.5 */}
            <input type="search" className="input flex-1" value={knowledgeQuery} onChange={e => setKnowledgeQuery(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleSearchKnowledge(); }} placeholder="e.g. black pod disease treatment" aria-label="Knowledge search" /> {/* v2.5 */}
            <button onClick={handleSearchKnowledge} disabled={knowledgeLoading || !knowledgeQuery.trim()} className="btn-primary text-sm whitespace-nowrap"> {/* v2.5 */}
              {knowledgeLoading ? <><Spinner size="sm" /> Searching…</> : <><Icon.BookOpen className="w-4 h-4" /> Search</>} {/* v2.5 */}
            </button>
          </div>
          {knowledgeError && <Alert tone="danger" onClose={() => setKnowledgeError(null)}>{knowledgeError}</Alert>} {/* v2.5 */}
          {knowledgeResult && ( // v2.5
            <div className="space-y-2"> {/* v2.5 */}
              <p className="text-2xs text-ink-500"> {/* v2.5 */}
                {knowledgeResult.total || 0} results for "{knowledgeResult.query}" (search_type: {knowledgeResult.search_type || 'hybrid'})
              </p>
              {knowledgeResult.results && Array.isArray(knowledgeResult.results) && knowledgeResult.results.length > 0 ? ( // v2.5
                knowledgeResult.results.map((r: any, i: number) => ( // v2.5
                  <div key={i} className="p-3 surface-inset rounded-lg space-y-1"> {/* v2.5 */}
                    <div className="flex items-center justify-between gap-2 flex-wrap"> {/* v2.5 */}
                      <h4 className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate"> {/* v2.5 */}
                        {r.document_title || r.title || r.chunk_text?.slice(0, 60) || `Chunk ${r.chunk_id?.slice(0, 8) || i}`} {/* v2.5 */}
                      </h4>
                      {r.similarity !== undefined && r.similarity !== null && ( // v2.5
                        <StatusPill tone="info" dot>Score {Number(r.similarity).toFixed(2)}</StatusPill> // v2.5
                      )}
                    </div>
                    {r.chunk_text && <p className="text-sm text-ink-700 dark:text-ink-300 text-pretty line-clamp-4">{r.chunk_text}</p>} {/* v2.5 */}
                    {r.document_url && <a href={r.document_url} target="_blank" rel="noreferrer" className="text-2xs text-info-600 dark:text-info-400 hover:underline inline-flex items-center gap-1"><Icon.ArrowUpRight className="w-3 h-3" /> Open source</a>} {/* v2.5 */}
                  </div>
                ))
              ) : ( // v2.5
                <EmptyState icon={<Icon.BookOpen className="w-5 h-5" />} title="No results" description="Try a different query or ingest documents in Admin → Knowledge." /> // v2.5
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════ */
/* v3.2.22: SMART ADVISORY SUMMARY CARD (Overview teaser)           */
/* ════════════════════════════════════════════════════════════════ */
/* Enterprise-grade summary card for the Overview tab. Replaces the
 * old duplicate AdvisoryTimelineCard + AgronomistAdvisoryCard that
 * was causing visual duplication between Overview and Smart Advisory.
 *
 * This card:
 *   1. Fetches the farmer's advisory timeline (lightweight — just the
 *      summary + count of urgent alerts + today's top action).
 *   2. Shows a compact, high-contrast summary with:
 *      - Number of urgent alerts (red/orange/yellow badge)
 *      - Today's top recommended action (text + threat icon)
 *      - AI model used + cache status
 *   3. Has a prominent "Open full 7-day plan →" CTA button.
 *
 * All text uses high-contrast color classes (text-ink-900 dark:text-ink-50)
 * so it's fully visible in both light AND dark mode — fixing the
 * "font color not permitting every text to be seen" issue.
 * ════════════════════════════════════════════════════════════════ */
const SmartAdvisorySummaryCard: React.FC<{
  farmId?: string;
  onOpenFullPlan: () => void;
}> = ({ farmId, onOpenFullPlan }) => {
  const [timeline, setTimeline] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient.get('/advisory-timeline/me?lang=en')
      .then(res => { if (!cancelled) setTimeline(res.data); })
      .catch(err => {
        if (!cancelled) setError(err.response?.data?.error || err.response?.data?.message || 'Unable to load advisory');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Compute summary metrics from the timeline
  const urgentCount = timeline?.urgent_alerts?.length || 0;
  const todayActions = (timeline?.timeline || []).filter((a: any) => a.day_offset === 0);
  const topAction = todayActions
    .sort((a: any, b: any) => {
      const p: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
      return (p[a.priority] ?? 4) - (p[b.priority] ?? 4);
    })[0];
  const totalActions = (timeline?.timeline || []).length;

  const threatIconMap: Record<string, string> = {
    black_pod: '🍂', cssv: '🦠', mirid: '🐛', mistletoe: '🌿',
    weather: '🌧️', harvest: '📦', sanitation: '🧹', pruning: '✂️', planting: '🌱', other: '📋',
  };
  const priorityBadge: Record<string, string> = {
    critical: 'bg-danger-100 text-danger-800 dark:bg-danger-500/20 dark:text-danger-300 border-danger-300 dark:border-danger-700',
    high: 'bg-warning-100 text-warning-800 dark:bg-warning-500/20 dark:text-warning-300 border-warning-300 dark:border-warning-700',
    medium: 'bg-info-100 text-info-800 dark:bg-info-500/20 dark:text-info-300 border-info-300 dark:border-info-700',
    low: 'bg-success-100 text-success-800 dark:bg-success-500/20 dark:text-success-300 border-success-300 dark:border-success-700',
  };

  return (
    <Card padding="lg" className="relative overflow-hidden">
      {/* v3.2.22: Subtle gradient background for visual hierarchy */}
      <div className="absolute inset-0 bg-gradient-to-br from-success-50/50 via-transparent to-info-50/30 dark:from-success-900/10 dark:via-transparent dark:to-info-900/10 pointer-events-none" />

      <div className="relative">
        <SectionHeader
          eyebrow="AI-powered · weather-driven"
          title="Smart Advisory"
          subtitle="Daily action plan tailored to your farm's weather + disease risk."
          icon={<Icon.Activity className="w-4 h-4" />}
          actions={
            <button
              onClick={onOpenFullPlan}
              className="btn-accent text-xs inline-flex items-center gap-1.5 px-3 py-1.5"
            >
              <Icon.Sparkles className="w-3 h-3" />
              Open full 7-day plan →
            </button>
          }
        />

        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 py-4">
              <Spinner size="sm" />
              <span className="text-sm text-ink-600 dark:text-ink-300">Generating your personalized advisory…</span>
            </div>
          ) : error ? (
            <div className="p-3 rounded-lg border border-warning-300 dark:border-warning-700 bg-warning-50 dark:bg-warning-900/20">
              <p className="text-sm font-medium text-warning-800 dark:text-warning-300">⚠ Advisory unavailable</p>
              <p className="text-xs text-warning-700 dark:text-warning-400 mt-1">{error}</p>
              <button
                onClick={onOpenFullPlan}
                className="mt-2 text-xs font-medium text-warning-800 dark:text-warning-300 hover:underline"
              >
                Try generating in the full tab →
              </button>
            </div>
          ) : !timeline ? (
            <div className="p-3 rounded-lg border border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-800/50">
              <p className="text-sm text-ink-700 dark:text-ink-300">No advisory timeline generated yet.</p>
              <button
                onClick={onOpenFullPlan}
                className="mt-2 text-xs font-medium text-success-700 dark:text-success-400 hover:underline"
              >
                Open Smart Advisory to generate →
              </button>
            </div>
          ) : (
            <>
              {/* Summary banner — high contrast */}
              <div className="p-3 rounded-lg bg-ink-900 dark:bg-ink-100 text-ink-50 dark:text-ink-900">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-2xs font-semibold uppercase tracking-micro opacity-80 mb-1">
                      AI Advisory · {timeline.cached ? 'Cached today' : 'Just generated'}
                    </p>
                    <p className="text-sm leading-relaxed">{timeline.summary || 'Personalized action plan ready.'}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-ink-50/20 dark:bg-ink-900/20 text-2xs font-medium shrink-0">
                    {timeline.ai_model || 'AI'}
                  </span>
                </div>
              </div>

              {/* Metric chips — high contrast, visible in dark mode */}
              <div className="grid grid-cols-3 gap-2">
                <div className={`p-2.5 rounded-lg border text-center ${urgentCount > 0 ? 'border-danger-300 dark:border-danger-700 bg-danger-50 dark:bg-danger-900/20' : 'border-success-300 dark:border-success-700 bg-success-50 dark:bg-success-900/20'}`}>
                  <p className={`text-2xl font-bold ${urgentCount > 0 ? 'text-danger-700 dark:text-danger-400' : 'text-success-700 dark:text-success-400'}`}>{urgentCount}</p>
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-600 dark:text-ink-400">Urgent</p>
                </div>
                <div className="p-2.5 rounded-lg border border-info-300 dark:border-info-700 bg-info-50 dark:bg-info-900/20 text-center">
                  <p className="text-2xl font-bold text-info-700 dark:text-info-400">{todayActions.length}</p>
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-600 dark:text-ink-400">Today</p>
                </div>
                <div className="p-2.5 rounded-lg border border-ink-300 dark:border-ink-600 bg-ink-50 dark:bg-ink-800/50 text-center">
                  <p className="text-2xl font-bold text-ink-800 dark:text-ink-200">{totalActions}</p>
                  <p className="text-2xs font-medium uppercase tracking-micro text-ink-600 dark:text-ink-400">7-day</p>
                </div>
              </div>

              {/* Today's top action — high contrast */}
              {topAction ? (
                <div className="p-3 rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p className="text-2xs font-semibold uppercase tracking-micro text-ink-500 dark:text-ink-400">Today's top priority</p>
                    <span className={`text-2xs font-medium px-2 py-0.5 rounded border ${priorityBadge[topAction.priority] || priorityBadge.low}`}>
                      {topAction.priority}
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-lg leading-none mt-0.5">{threatIconMap[topAction.threat] || '📋'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">{topAction.action}</p>
                      {topAction.time_window && (
                        <p className="text-2xs text-ink-600 dark:text-ink-400 mt-0.5">
                          ⏰ {topAction.time_window}
                          {topAction.product && <span> · {topAction.product}</span>}
                          {topAction.dosage && <span> · {topAction.dosage}</span>}
                        </p>
                      )}
                      {topAction.reason && (
                        <p className="text-xs text-ink-700 dark:text-ink-300 mt-1 italic">{topAction.reason}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 rounded-lg border border-success-300 dark:border-success-700 bg-success-50 dark:bg-success-900/20">
                  <p className="text-sm text-success-800 dark:text-success-300 font-medium">✓ No critical actions for today</p>
                  <p className="text-xs text-success-700 dark:text-success-400 mt-0.5">Check the 7-day plan for upcoming tasks.</p>
                </div>
              )}

              {/* Urgent alerts preview (first 2) */}
              {timeline.urgent_alerts && timeline.urgent_alerts.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-2xs font-semibold uppercase tracking-micro text-ink-500 dark:text-ink-400">Urgent alerts ({timeline.urgent_alerts.length})</p>
                  {timeline.urgent_alerts.slice(0, 2).map((alert: any, i: number) => (
                    <div
                      key={i}
                      className={`p-2 rounded-lg border-l-4 ${
                        alert.severity === 'critical'
                          ? 'bg-danger-50 dark:bg-danger-900/20 border-danger-500'
                          : alert.severity === 'warning'
                          ? 'bg-warning-50 dark:bg-warning-900/20 border-warning-500'
                          : 'bg-info-50 dark:bg-info-900/20 border-info-500'
                      }`}
                    >
                      <p className="text-xs font-semibold text-ink-900 dark:text-ink-50">{alert.title}</p>
                      <p className="text-2xs text-ink-700 dark:text-ink-300 mt-0.5 line-clamp-2">{alert.message}</p>
                    </div>
                  ))}
                  {timeline.urgent_alerts.length > 2 && (
                    <p className="text-2xs text-ink-500 dark:text-ink-400 text-center">
                      + {timeline.urgent_alerts.length - 2} more alert(s) — open full plan to view
                    </p>
                  )}
                </div>
              )}

              {/* CTA footer — high contrast */}
              <button
                onClick={onOpenFullPlan}
                className="w-full mt-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-success-600 to-info-600 hover:from-success-700 hover:to-info-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md"
              >
                <Icon.Activity className="w-4 h-4" />
                View full 7-day action plan
                <span className="text-xs opacity-80">→</span>
              </button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

/* ════════════════════════════════════════════════════════════════ */
/* v2.9: AGRONOMIST ADVISORY CARD                                   */
/* ════════════════════════════════════════════════════════════════ */
/* Fallback "fresh advisory" generator — calls the AGRONOMIST agent  */
/* (POST /api/ai-agents/AGRONOMIST/run) with the farmer's primary    */
/* farm data. Renders the AI-generated recommendations inline. Use    */
/* when the standard Advisory Timeline is empty/unavailable.          */
/* ════════════════════════════════════════════════════════════════ */
const AgronomistAdvisoryCard: React.FC<{ farm: any | null }> = ({ farm }) => { // v2.9
  const [loading, setLoading] = useState(false); // v2.9
  const [result, setResult] = useState<any>(null); // v2.9
  const [error, setError] = useState<string | null>(null); // v2.9

  const handleGenerate = useCallback(async () => { // v2.9
    setLoading(true); // v2.9
    setError(null); // v2.9
    try { // v2.9
      // v2.9: POST /api/ai-agents/AGRONOMIST/run with the farmer's primary farm data
      const payload: any = { // v2.9
        farmId: farm?.id, // v2.9
        areaHectares: farm?.area_hectares ?? farm?.area_ha, // v2.9
        cocoaVariety: farm?.cocoa_variety ?? farm?.variety, // v2.9
        region: farm?.village ?? farm?.region, // v2.9
      }; // v2.9
      const res = await apiClient.post('/ai-agents/AGRONOMIST/run', payload); // v2.9
      setResult(res.data); // v2.9
    } catch (err: any) { // v2.9
      setError(err?.response?.data?.error || err?.message || 'Failed to generate AI advisory.'); // v2.9
    } finally { // v2.9
      setLoading(false); // v2.9
    }
  }, [farm]); // v2.9

  return ( // v2.9
    <div className="farmer-agronomist-card"> {/* v3.2.22: wrapper div instead of nested Card (parent already wraps in Card) */}
      <SectionHeader // v2.9
        eyebrow="AI Agronomist · on-demand" // v3.2.22: clearer label
        title="Generate a fresh AI advisory" // v2.9
        subtitle="If the 7-day timeline above is empty, ask the AI Agronomist for immediate recommendations based on your farm data." // v2.9
        icon={<Icon.Activity className="w-4 h-4" />} // v2.9
      /> {/* v2.9 */}
      <div className="mt-4 space-y-3"> {/* v2.9 */}
        {error && <Alert tone="danger" onClose={() => setError(null)}>{error}</Alert>} {/* v2.9 */}
        <button
          onClick={handleGenerate}
          disabled={loading || !farm}
          className="w-full py-2.5 px-4 rounded-lg bg-gradient-to-r from-info-600 to-info-700 hover:from-info-700 hover:to-info-800 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <><Spinner size="sm" /> <span>AI Agronomist is analyzing your farm…</span></> : <><Icon.Sparkles className="w-4 h-4" /> <span>Ask AI Agronomist</span></>}
        </button>
        {!farm && (
          <p className="text-xs text-ink-600 dark:text-ink-400 text-center p-2 rounded-lg bg-ink-50 dark:bg-ink-800/50">
            Map a farm first to enable AI agronomy recommendations.
          </p>
        )}

        {/* v2.9: render agent output — v3.2.22: high-contrast colors throughout */}
        {result?.success && result?.output && ( // v2.9
          <div className="space-y-3 mt-2"> {/* v2.9 */}
            {Array.isArray(result.output.recommendations) && result.output.recommendations.length > 0 && ( // v2.9
              <div className="p-3 rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800"> {/* v3.2.22: high-contrast */}
                <p className="text-2xs font-semibold uppercase tracking-micro text-success-800 dark:text-success-300 mb-2">Recommendations</p>
                <ul className="space-y-1.5">
                  {result.output.recommendations.map((rec: any, i: number) => (
                    <li key={i} className="text-sm text-ink-800 dark:text-ink-100 text-pretty flex gap-2">
                      <span className="text-success-700 dark:text-success-400 shrink-0 font-bold">•</span>
                      <span>{typeof rec === 'string' ? rec : (rec.action || rec.title || JSON.stringify(rec))}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.output.pruning_schedule && (
              <div className="p-3 rounded-lg bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-700">
                <p className="text-2xs font-semibold uppercase tracking-micro text-ink-700 dark:text-ink-300 mb-1">Pruning Schedule</p>
                <p className="text-sm text-ink-800 dark:text-ink-100 text-pretty">{result.output.pruning_schedule}</p>
              </div>
            )}
            {result.output.shade_management && (
              <div className="p-3 rounded-lg bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-700">
                <p className="text-2xs font-semibold uppercase tracking-micro text-ink-700 dark:text-ink-300 mb-1">Shade Management</p>
                <p className="text-sm text-ink-800 dark:text-ink-100 text-pretty">{result.output.shade_management}</p>
              </div>
            )}
            {result.output.harvest_timing && (
              <div className="p-3 rounded-lg bg-ink-50 dark:bg-ink-800/50 border border-ink-200 dark:border-ink-700">
                <p className="text-2xs font-semibold uppercase tracking-micro text-ink-700 dark:text-ink-300 mb-1">Harvest Timing</p>
                <p className="text-sm text-ink-800 dark:text-ink-100 text-pretty">{result.output.harvest_timing}</p>
              </div>
            )}
            {result.output.reasoning && (
              <details className="border-t border-ink-200 dark:border-ink-700 pt-2">
                <summary className="text-xs font-medium text-ink-700 dark:text-ink-300 cursor-pointer hover:text-ink-900 dark:hover:text-ink-100">AI reasoning</summary>
                <p className="mt-2 text-xs text-ink-700 dark:text-ink-300 text-pretty p-2 rounded bg-ink-50 dark:bg-ink-800/50">{result.output.reasoning}</p>
              </details>
            )}
            <p className="text-2xs text-ink-600 dark:text-ink-400 text-center font-medium">
              Generated by {result.model || 'gemini-2.0-flash'} · {result.durationMs ? `${result.durationMs} ms` : ''}
            </p>
          </div>
        )}
        {result && !result.success && (
          <Alert tone="danger">{result.error || 'AI Agronomist failed to generate an advisory.'}</Alert>
        )}
      </div>
    </div>
  );
}; // v2.9

// ════════════════════════════════════════════════════════════════
// v3.2.6: FarmerMarketTab — Live cocoa prices + AI market analysis
// ════════════════════════════════════════════════════════════════
// Shows the farmer the latest cocoa prices, 7-day trend, regional
// differentials, and an AI-generated market advisory (when to sell,
// hold, or wait). Calls POST /api/ai-agents/MARKET/run for the AI analysis.
const FarmerMarketTab: React.FC<{ villageName: string }> = ({ villageName }) => {
  const [prices, setPrices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    fetchPrices();
  }, []);

  const fetchPrices = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/data-sources/market-prices/list?limit=30'); // v3.2.6
      setPrices(res.data?.prices || res.data?.rows || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load market prices');
    } finally {
      setLoading(false);
    }
  };

  const runAiAnalysis = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiAnalysis(null);
    try {
      const res = await apiClient.post('/ai-agents/MARKET/run', {
        region: villageName || 'Cameroon',
      });
      // v3.2.10: Check for agent-internal failure (success: false)
      if (res.data?.success === false) {
        setAiError(res.data?.error || res.data?.message || 'AI analysis failed. Check that an AI API key is configured in Admin > API Keys.');
      } else {
        setAiAnalysis(res.data);
      }
    } catch (err: any) {
      const hint = err.response?.data?.hint;
      setAiError(err.response?.data?.message || err.response?.data?.error || 'AI analysis failed' + (hint ? `\n\n${hint}` : ''));
    } finally {
      setAiLoading(false);
    }
  };

  const latestPrice = prices[0];
  const avgPrice = prices.length > 0 ? prices.reduce((sum, p) => sum + (parseFloat(p.price_xaf_per_kg) || 0), 0) / prices.length : 0; // v3.2.8: removed price_per_kg fallback
  const maxPrice = prices.length > 0 ? Math.max(...prices.map(p => parseFloat(p.price_xaf_per_kg) || 0)) : 0;
  const minPrice = prices.length > 0 ? Math.min(...prices.map(p => parseFloat(p.price_xaf_per_kg) || 0)) : 0;

  return (
    <div className="farmer-tab-root farmer-mobile-v13 farmer-tab-market space-y-4">
      {/* Header */}
      <SectionHeader
        eyebrow="Market Intelligence"
        title="Cocoa Market Dashboard"
        subtitle="Live cocoa prices, 7-day trends, and AI-powered market analysis to help you decide when to sell."
        icon={<Icon.TrendingUp className="w-4 h-4" />}
      />

      {/* Price summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Latest Price" value={latestPrice ? `${parseFloat(latestPrice.price_xaf_per_kg || 0).toLocaleString()} XAF/kg` : '—'} accent="cargo" />
        <MetricTile label="30-Day Average" value={avgPrice > 0 ? `${Math.round(avgPrice).toLocaleString()} XAF/kg` : '—'} accent="admin" />
        <MetricTile label="30-Day High" value={maxPrice > 0 ? `${Math.round(maxPrice).toLocaleString()} XAF/kg` : '—'} accent="analytics" />
        <MetricTile label="30-Day Low" value={minPrice > 0 ? `${Math.round(minPrice).toLocaleString()} XAF/kg` : '—'} accent="cocoa" />
      </div>

      {/* Price history table */}
      <Card padding="lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">Recent Price History</h3>
          <button onClick={fetchPrices} disabled={loading} className="btn-secondary text-xs">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {error && <Alert tone="danger">{error}</Alert>}

        {loading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : prices.length === 0 ? (
          <EmptyState icon={<Icon.TrendingUp className="w-5 h-5" />} title="No price data yet" description="Market prices will appear here once the admin syncs data sources (DS003 ONCC, DS006 ICCO)." />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table mobile-record-table farmer-market-records">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Price (XAF/kg)</th>
                  <th>Source</th>
                  <th>Region</th>
                </tr>
              </thead>
              <tbody>
                {prices.slice(0, 15).map((p, i) => (
                  <tr key={i}>
                    <td className="text-xs">{new Date(p.price_date || p.created_at).toLocaleDateString()}</td>
                    <td className="text-sm font-semibold nums">{parseFloat(p.price_xaf_per_kg || 0).toLocaleString()}</td>
                    <td className="text-2xs text-ink-500">{p.country || p.region || 'Cameroon'}</td>
                    <td className="text-2xs text-ink-500">{p.region || p.country || 'Cameroon'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* AI Market Analysis */}
      <Card padding="lg" className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-ink-900 dark:text-ink-50">AI Market Analysis</h3>
            <p className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">Get AI-powered advice on when to sell your cocoa</p>
          </div>
          <button onClick={runAiAnalysis} disabled={aiLoading} className="btn-primary text-xs inline-flex items-center gap-1.5">
            {aiLoading ? <Spinner size="sm" /> : <Icon.Activity className="w-3 h-3" />}
            {aiLoading ? 'Analyzing market...' : 'Analyze with AI'}
          </button>
        </div>

        {aiError && <Alert tone="danger">{aiError}</Alert>}

        {aiAnalysis?.success && aiAnalysis.output && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-2xs text-ink-500">Model: {aiAnalysis.model || 'unknown'}</span>
              {aiAnalysis.output.confidence && (
                <StatusPill tone={aiAnalysis.output.confidence > 70 ? 'success' : 'warning'} dot>
                  Confidence: {aiAnalysis.output.confidence}%
                </StatusPill>
              )}
            </div>
            {aiAnalysis.output.current_trend && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500 mb-1">Current Trend</p>
                <p className="text-sm font-medium">{aiAnalysis.output.current_trend}</p>
              </div>
            )}
            {aiAnalysis.output.timing_advice && (
              <div className="p-3 rounded-md bg-green-50 dark:bg-green-950/30 border-l-4 border-green-500">
                <p className="text-2xs text-ink-500 mb-1">When to Sell</p>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">{aiAnalysis.output.timing_advice}</p>
              </div>
            )}
            {aiAnalysis.output.price_forecast_7d && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500 mb-1">7-Day Forecast</p>
                <p className="text-sm">{aiAnalysis.output.price_forecast_7d}</p>
              </div>
            )}
            {aiAnalysis.output.regional_differential && (
              <div className="p-3 rounded-md bg-ink-50 dark:bg-ink-900">
                <p className="text-2xs text-ink-500 mb-1">Regional Price Differences</p>
                <p className="text-xs">{aiAnalysis.output.regional_differential}</p>
              </div>
            )}
            {aiAnalysis.output.reasoning && (
              <details className="text-xs">
                <summary className="cursor-pointer text-ink-500">AI Reasoning</summary>
                <p className="mt-2 text-ink-700 dark:text-ink-300">{aiAnalysis.output.reasoning}</p>
              </details>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};
